import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import OT from '@vonage/client-sdk-video';
import { toast } from 'react-toastify';
import SignatureCanvas from 'react-signature-canvas';
import { FileSignature, Loader2, RotateCcw } from 'lucide-react';
import {
  MicIcon,
  MicOffIcon,
  VideoIcon,
  VideoOffIcon,
  CallEndedIcon,
  CheckCircleSvgIcon,
  VideoCallPhnIcon,
  VideoCallGenderIcon,
  VideoCallDobIcon,
  VideoCallAddressIcon,
} from '../../assets/svg/icons';
import PictureInPictureAltRoundedIcon from '@mui/icons-material/PictureInPictureAltRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import MicrophoneSelector, {
  DEFAULT_MICROPHONE_ID,
  readStoredMicrophonePreference,
} from './MicrophoneSelector';
import SpeakerSelector, {
  DEFAULT_SPEAKER_ID,
  readStoredSpeakerPreference,
} from './SpeakerSelector';
import CameraSelector, {
  DEFAULT_CAMERA_ID,
  readStoredCameraPreference,
} from './CameraSelector';
import { buildStoredPreference } from './mediaDeviceSelectorUtils';
import {
  RELIABLE_VIDEO_PUBLISHER_OPTIONS,
  applyPublisherNetworkProfile,
} from '../../utils/vonagePublisherQuality';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  fetchPortalPatientActiveMedications,
  fetchPortalPatientPrescriptionDetails,
  fetchPortalPatientProfile,
  fetchPortalPatientAllRx,
  fetchPatientMedicineByDin,
  savePortalDeliveryVideoCallCounselling,
  savePortalDeliveryVideoCallImage,
  signPortalDeliveryVideoCallPrescriptions,
  verifyPortalDeliveryVideoCallPatient,
} from './deliveryVideoCallApi';
import { getCurrentDateInAppTimezone } from '../../utils/constants/formInitialStates';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import {
  createDeliveryCallStreamSource,
  isDeliveryCallStreamEnabled,
} from './deliveryCallStreamSource';
import { useAdminStealthHidden } from '../../hooks/useAdminStealth';
import {
  LIVEKIT_IGNORABLE_PATTERNS,
  LIVEKIT_PERMISSION_PATTERNS,
  createLiveKitSession,
  hasLiveKitCredentials,
  isMeditalkCall,
  listLiveKitDevices,
} from './livekitSession';

const SCREENSHOT_CAPTURE_INTERVAL_MS = 10000;
const PHARMACY_TIME_ZONE = process.env.REACT_APP_PHARMACY_TIME_ZONE || 'Canada/Pacific';

const getPatientProfileData = (payload) => payload?.data ?? payload?.patient ?? payload ?? {};

const getMedicationList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.medicines)) return payload.medicines;
  return [];
};

const getPrescriptionList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) {
    return payload.data.flatMap((item) =>
      Array.isArray(item?.prescriptions) ? item.prescriptions : []
    );
  }
  if (Array.isArray(payload?.data?.data)) {
    return payload.data.data.flatMap((item) =>
      Array.isArray(item?.prescriptions) ? item.prescriptions : []
    );
  }
  if (Array.isArray(payload?.medicines)) return payload.medicines;
  return [];
};

const getDinMedicationDetail = (payload) => {
  if (Array.isArray(payload?.data)) return payload.data[0] ?? null;
  if (Array.isArray(payload?.data?.data)) return payload.data.data[0] ?? null;
  if (Array.isArray(payload)) return payload[0] ?? null;
  return payload ?? null;
};

const hasListData = (value) => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') {
    if (Array.isArray(value?.data)) {
      return value.data.some((item) =>
        Array.isArray(item?.prescriptions) ? item.prescriptions.length > 0 : false
      );
    }
    if (Array.isArray(value?.data?.data)) {
      return value.data.data.some((item) =>
        Array.isArray(item?.prescriptions) ? item.prescriptions.length > 0 : false
      );
    }
    if (Array.isArray(value?.medicines)) return value.medicines.length > 0;
  }
  return false;
};

const getTodayInPharmacyTimezone = () => {
  try {
    return getCurrentDateInAppTimezone();
  } catch (error) {
    return new Date().toISOString().slice(0, 10);
  }
};

const normalizeRx = (value) => String(value ?? '').trim();

const isTruthyMedicationFlag = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return ['1', 'true', 'yes', 'y'].includes(normalized);
  }
  return false;
};

const medicationName = (med) =>
  med?.drug ||
  med?.trade_name ||
  med?.medicine_name ||
  med?.drug_name ||
  med?.name ||
  'Medication';

const medicationRx = (med) => med?.rxnumber || med?.rx_number || med?.rx || med?.rx_number_id || 'N/A';

const medicationSig = (med) =>
  med?.sig ||
  med?.rxsig ||
  med?.rx_sig ||
  med?.directions ||
  med?.instruction ||
  med?.instructions ||
  med?.dose_instruction ||
  'No sig available';

const medicationQty = (med) =>
  med?.processing ??
  med?.delivery_qty ??
  med?.refill_reqty ??
  med?.qty_prev ??
  med?.qty_left ??
  null;

const medicationDin = (med) => {
  const dinValue =
    med?.din ??
    med?.DIN ??
    med?.din_number ??
    med?.dinNumber ??
    med?.drug_din ??
    med?.drugDin ??
    '';

  const normalizedDin = String(dinValue).trim();
  return normalizedDin;
};

const medicationPrescriptionId = (med) =>
  med?.id ?? med?.winrx_data_id ?? med?.winrxDataId ?? med?.winrxdataid ?? null;

const medicationHasPharmacistSignature = (med) =>
  Boolean(med?.f_sign_pharmasist_id && med?.f_pharmasist_sign_name);

const formatMedicationQty = (value) => {
  if (value === undefined || value === null || value === '') return '';

  const rawValue = String(value).trim();
  if (!rawValue) return '';

  const numericValue = Number(rawValue);
  if (!Number.isFinite(numericValue)) return rawValue;

  if (Number.isInteger(numericValue)) {
    return String(numericValue);
  }

  return rawValue.includes('.')
    ? rawValue.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '')
    : String(numericValue);
};

const getPharmacyCurrentDateTimeParts = () => {
  const currentTime = new Date();

  const options = {
    timeZone: PHARMACY_TIME_ZONE,
    hour12: true,
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };

  const full = currentTime.toLocaleString('en-US', options);
  const lastComma = full.lastIndexOf(', ');

  if (lastComma === -1) {
    return { datePart: full, timePart: '' };
  }

  return {
    datePart: full.slice(0, lastComma),
    timePart: full.slice(lastComma + 2),
  };
};

const formatVerificationStamp = (verifiedAt) => {
  if (!verifiedAt) return { datePart: '', timePart: '' };

  const parsed = new Date(verifiedAt);
  if (Number.isNaN(parsed.getTime())) {
    return { datePart: String(verifiedAt), timePart: '' };
  }

  const formatted = parsed.toLocaleString('en-US', {
    timeZone: PHARMACY_TIME_ZONE,
    hour12: true,
    hour: 'numeric',
    minute: 'numeric',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const lastComma = formatted.lastIndexOf(', ');
  if (lastComma === -1) return { datePart: formatted, timePart: '' };

  return {
    datePart: formatted.slice(0, lastComma),
    timePart: formatted.slice(lastComma + 2),
  };
};

const normalizePatientVerificationPayload = (verification, fallbackStamp = '') => {
  if (!verification && !fallbackStamp) return null;

  const verifiedAt =
    verification?.verified_at ??
    verification?.verifiedAt ??
    verification?.recorded_at ??
    verification?.recordedAt ??
    '';
  const verifiedByName =
    verification?.verified_by_name ??
    verification?.verifiedByName ??
    verification?.recorded_by_name ??
    verification?.recordedByName ??
    '';
  const verifiedStamp =
    verification?.verified_stamp ??
    verification?.verifiedStamp ??
    fallbackStamp ??
    '';
  const verifiedFlag =
    verification?.verified ??
    verification?.is_verified ??
    verification?.isVerified;
  const verified = Boolean(verifiedFlag ?? verifiedAt ?? verifiedStamp);

  return {
    ...(verification ?? {}),
    verified,
    verified_at: verifiedAt,
    verified_by_name: verifiedByName,
    verified_stamp: verifiedStamp,
  };
};

const isNarcoticMedication = (med) =>
  isTruthyMedicationFlag(med?.narcotic) ||
  isTruthyMedicationFlag(med?.is_narcotic) ||
  isTruthyMedicationFlag(med?.narc) ||
  isTruthyMedicationFlag(med?.controlled);

const medicationHasWitness = (med) => {
  const witnessValue = med?.rx_witness;
  return typeof witnessValue === 'string' && witnessValue.trim().toUpperCase() === 'Y';
};

const getQuickCounsellingTips = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        if (item && typeof item === 'object') {
          return String(
            item.quick_counselling_tip ??
            item.quickCounsellingTip ??
            item.label ??
            item.value ??
            ''
          ).trim();
        }
        return '';
      })
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return getQuickCounsellingTips(parsed);
      }
    } catch (error) { }

    return trimmed
      .split(/\r?\n|•|·/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const medicationIsPendingCounselling = (med) => {
  const note = String(med?.pending_counseling_note ?? '').trim().toLowerCase();
  const status = String(med?.status ?? med?.delivery_status ?? '').trim().toLowerCase();

  return (
    isTruthyMedicationFlag(med?.pending_counseling) &&
    !isTruthyMedicationFlag(med?.counsling_provided) &&
    note === 'consultation provided' &&
    ['pending'].includes(status)
  );
};

const medicationHasDisplayData = (medication) => {
  const name = medicationName(medication);
  const rx = medicationRx(medication);
  const sig = medicationSig(medication);
  const qty = medicationQty(medication);

  return (
    (name && name !== 'Medication') ||
    (rx && rx !== 'N/A') ||
    (sig && sig !== 'No sig available') ||
    qty !== null
  );
};

// These matchers are how the call decides "transient glitch" vs "the call is
// broken". The vocabulary was written against OpenTok/SessionDispatcher wording;
// LiveKit produces none of those strings, so its equivalents are appended here
// or the matchers silently stop catching what they were written for.
const VONAGE_IGNORABLE_PATTERNS = [
  'OT_CANCEL',
  'getUserMedia request was aborted',
  'Unable to Publish',
  'GetUserMedia',
  'Publisher State Change Failed',
  'Destroyed.+Failed',
  'connectivity event.+Failure',
];

const IGNORABLE_ISSUE_PATTERN = new RegExp(VONAGE_IGNORABLE_PATTERNS.join('|'), 'i');
// Only ever applied to MediTalk calls. Kept off the Vonage regex on purpose:
// some of these strings are generic enough to change which OpenTok errors are
// swallowed, and the Vonage path has to behave exactly as it did.
const IGNORABLE_ISSUE_PATTERN_LIVEKIT = new RegExp(
  [...VONAGE_IGNORABLE_PATTERNS, ...LIVEKIT_IGNORABLE_PATTERNS].join('|'),
  'i'
);

const isIgnorableVonageIssue = (value, includeLiveKit = false) => {
  const text = [
    value?.title,
    value?.message,
    value?.name,
    value?.code,
    value,
  ].filter(Boolean).join(' ');

  return (includeLiveKit ? IGNORABLE_ISSUE_PATTERN_LIVEKIT : IGNORABLE_ISSUE_PATTERN).test(text);
};

const VONAGE_PERMISSION_PATTERNS = [
  'OT_USER_MEDIA_ACCESS_DENIED',
  'NotAllowedError',
  'Permission denied',
  'denied permission to (?:hardware|use)',
  'PermissionDeniedError',
];

const PERMISSION_DENIED_PATTERN = new RegExp(VONAGE_PERMISSION_PATTERNS.join('|'), 'i');
const PERMISSION_DENIED_PATTERN_LIVEKIT = new RegExp(
  [...VONAGE_PERMISSION_PATTERNS, ...LIVEKIT_PERMISSION_PATTERNS].join('|'),
  'i'
);

// A denied camera/mic is NOT a failed call: the session is connected and the
// remote participant's video still works. It must degrade to view-only rather
// than tearing the call down.
const isPermissionDeniedIssue = (value, includeLiveKit = false) => {
  const text = [
    value?.title,
    value?.message,
    value?.name,
    value?.code,
    value,
  ].filter(Boolean).join(' ');

  return (includeLiveKit ? PERMISSION_DENIED_PATTERN_LIVEKIT : PERMISSION_DENIED_PATTERN).test(text);
};

// Browser autoplay policy blocks a media element from playing before any user
// gesture. When a call is accepted without a click (auto-pickup) or the
// pharmacist denied their devices, the remote video's play() rejects with this.
// It is NOT a failed call — it just means the pharmacist must click once to
// hear audio — so it must be swallowed instead of crashing on an unhandled
// rejection.
const isAutoplayBlockedIssue = (value) => {
  const text = [
    value?.title,
    value?.message,
    value?.name,
    value?.code,
    value,
  ].filter(Boolean).join(' ');

  return /didn't interact with the document|play\(\) (?:failed|request was interrupted)|NotAllowedError|The play method is not allowed|autoplay/i.test(text);
};

const firstProfileValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== '') || 'N/A';

const buildPatientAddress = (profile = {}) => {
  const profileCandidates = [
    profile,
    profile?.patient,
    profile?.patient_details,
    profile?.patientDetails,
    profile?.details,
    profile?.profile,
    profile?.user,
  ].filter(Boolean);

  const findFirstValue = (...keys) => {
    for (const candidate of profileCandidates) {
      for (const key of keys) {
        const value = candidate?.[key];
        if (value !== undefined && value !== null && String(value).trim() !== '') {
          return value;
        }
      }
    }
    return '';
  };

  const rawLineParts = [
    findFirstValue('fullAddress', 'full_address', 'formatted_address', 'patient_address'),
    findFirstValue('address'),
    findFirstValue('address_1', 'address1', 'street_address', 'street', 'street1'),
    findFirstValue('address_2', 'address2', 'apartment', 'unit', 'unit_number', 'suite'),
  ].filter((value) => value !== undefined && value !== null && String(value).trim() !== '');

  const lineParts = rawLineParts.filter((value, index, array) => {
    const normalizedValue = String(value).trim().toLowerCase();
    return array.findIndex((item) => String(item).trim().toLowerCase() === normalizedValue) === index;
  });

  const localityParts = [
    findFirstValue('city'),
    findFirstValue('province', 'state'),
    findFirstValue('postal_code', 'postcode', 'zip', 'zip_code', 'zipcode'),
  ].filter((value) => value !== undefined && value !== null && String(value).trim() !== '');

  return [...lineParts, ...localityParts].join(' ').trim();
};

const ControlButton = ({
  onClick,
  variant = 'neutral',
  active = true,
  title,
  className = '',
  children,
}) => {
  const variantClass =
    variant === 'danger'
      ? 'rounded-full w-10 h-10 flex-shrink-0 text-[#e4e4e7] hover:opacity-90 border-0'
      : active
        ? 'rounded-full w-10 h-10 text-[#e4e4e7] hover:opacity-90 border-0'
        : 'rounded-full w-10 h-10 bg-red-500/20 text-red-500 hover:bg-red-500/30 border-0';

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-0 ${variantClass} ${className}`}
    >
      {children}
    </button>
  );
};

const getBrowserAudioRoutingSupport = () => {
  if (typeof navigator === 'undefined') {
    return {
      isIOSBrowser: false,
      isSafariBrowser: false,
    };
  }

  const userAgent = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const maxTouchPoints = navigator.maxTouchPoints || 0;
  const isIOSBrowser =
    /iPad|iPhone|iPod/i.test(userAgent) ||
    (platform === 'MacIntel' && maxTouchPoints > 1);
  const isSafariBrowser =
    /Safari/i.test(userAgent) &&
    !/Chrome|CriOS|Android|Edg|FxiOS|OPR/i.test(userAgent);

  return {
    isIOSBrowser,
    isSafariBrowser,
  };
};

const PortalDeliveryCallWorkspace = ({
  callData,
  pharmacistName,
  autoPickupEnabled = false,
  isAutoPickupUpdating = false,
  onDisableAutoPickup,
  onClose,
}) => {
  const [patientProfile, setPatientProfile] = useState(null);
  const [rawActiveMedications, setRawActiveMedications] = useState([]);
  const [dailyPrescriptions, setDailyPrescriptions] = useState([]);
  const [isPatientDataLoading, setIsPatientDataLoading] = useState(true);
  const [isMedicationListLoading, setIsMedicationListLoading] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isVideoOn, setIsVideoOn] = useState(true);
  // When the pharmacist is publishing the pre-recorded clip, this flips true
  // once they switch to their real camera/mic for the rest of the call.
  const [hasSwitchedToLiveVideo, setHasSwitchedToLiveVideo] = useState(false);
  const [isSwitchingToLiveVideo, setIsSwitchingToLiveVideo] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [mediaNotice, setMediaNotice] = useState(null);
  const [isRemoteCameraOff, setIsRemoteCameraOff] = useState(false);
  const [isRemoteCameraPermissionDenied, setIsRemoteCameraPermissionDenied] = useState(false);
  // True when the browser blocked autoplay of the remote audio (no user gesture
  // yet). The pharmacist taps "Enable sound" to unlock it.
  const [isCallAudioBlocked, setIsCallAudioBlocked] = useState(false);
  const [isPiPActive, setIsPiPActive] = useState(false);
  const [isPiPSupported, setIsPiPSupported] = useState(false);
  const [isAudioDevicePanelOpen, setIsAudioDevicePanelOpen] = useState(false);
  const [publisherInstance, setPublisherInstance] = useState(null);
  const [speakerPreference, setSpeakerPreference] = useState(() => readStoredSpeakerPreference());
  const [isRemotePortrait, setIsRemotePortrait] = useState(false);
  const [isCallConnected, setIsCallConnected] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 1024 : false
  );
  const [callDateTime, setCallDateTime] = useState(() => getPharmacyCurrentDateTimeParts());
  const [patientVerification, setPatientVerification] = useState(
    normalizePatientVerificationPayload(
      callData?.patient_verification,
      callData?.verified_stamp ?? callData?.patient_verification?.verified_stamp ?? ''
    )
  );
  const [verifiedStamp, setVerifiedStamp] = useState(callData?.verified_stamp ?? callData?.patient_verification?.verified_stamp ?? '');
  const [isSavingVerification, setIsSavingVerification] = useState(false);
  const [activeCounsellingRx, setActiveCounsellingRx] = useState(null);
  const [counsellingSelections, setCounsellingSelections] = useState({});
  const [isSavingCounselling, setIsSavingCounselling] = useState(false);
  const [counsellingSaveError, setCounsellingSaveError] = useState('');
  const [rxListTab, setRxListTab] = useState('Active');
  const [activeDinMedicationDetail, setActiveDinMedicationDetail] = useState(null);
  const [dinMedicationLoadingRx, setDinMedicationLoadingRx] = useState('');
  const [counsellingDrugImagePreview, setCounsellingDrugImagePreview] = useState(null);
  const [activeSigningPrescription, setActiveSigningPrescription] = useState(null);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isSavingPrescriptionSignature, setIsSavingPrescriptionSignature] = useState(false);
  const [prescriptionSignatureError, setPrescriptionSignatureError] = useState('');

  const publisherRef = useRef(null);
  const subscriberRef = useRef(null);
  const publisherContainerRef = useRef(null);
  const subscriberContainerRef = useRef(null);
  const screenshotCanvasRef = useRef(null);
  const screenshotIntervalRef = useRef(null);
  const screenshotUploadInFlightRef = useRef(false);
  const managedAudioStreamRef = useRef(null);
  const streamSourceRef = useRef(null);
  const livekitSessionRef = useRef(null);
  const isMicOnRef = useRef(true);
  const isVideoOnRef = useRef(true);
  const speakerPreferenceRef = useRef(speakerPreference);
  const activeCounsellingRxRef = useRef(null);
  const prescriptionSignaturePadRef = useRef(null);

  const patientDisplayName = useMemo(() => {
    const lastFirst = [callData?.patientLastName, callData?.patientFirstName].filter(Boolean).join(' ').trim();
    if (lastFirst) return lastFirst;
    const full = callData?.patientName?.trim();
    if (!full) return 'Patient';
    const parts = full.split(/\s+/);
    if (parts.length >= 2) return [parts[parts.length - 1], parts.slice(0, -1).join(' ')].join(' ');
    return full;
  }, [callData]);
  // ADMIN SCREEN PRIVACY (F9 x5, see useAdminStealth). While hidden, the
  // pre-recorded clip is not used: the call runs on the live camera and
  // microphone.
  //
  // Expressed by turning OFF the single flag the whole call already keys on,
  // so a hidden pharmacist takes the EXACT path a pharmacist with no clip
  // configured takes today — the ordinary camera/mic path that every call
  // without a clip has always run. No branch in the call is new, and nothing
  // about publishing, device selection or failure handling changes.
  //
  // `adminStealthHidden` is false for every non-admin and false whenever the
  // backend cannot answer, so for everybody else this line is a no-op and the
  // clip behaves exactly as before.
  const adminStealthHidden = useAdminStealthHidden();

  // True when the backend has this pharmacist set up to publish a pre-recorded
  // clip instead of their camera for this call.
  const usesStreamSource = isDeliveryCallStreamEnabled(callData) && !adminStealthHidden;
  // Which SDK this call belongs to. Pinned by the backend on the call row; for
  // MediTalk the vonage_session_id/vonage_token slots carry the LiveKit room
  // name and JWT, and application_id is deliberately empty.
  const isMeditalk = isMeditalkCall(callData);
  const canUsePictureInPicture =
    typeof document !== 'undefined' &&
    typeof document.pictureInPictureEnabled === 'boolean' &&
    document.pictureInPictureEnabled;
  const canSelectAudioOutput = isMeditalk
    // LiveKit routes output by setting sinkId on the media elements it created,
    // so support is exactly "does this browser have setSinkId".
    ? (typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype)
    : (typeof OT.getAudioOutputDevices === 'function' &&
      typeof OT.setAudioOutputDevice === 'function');
  const { isIOSBrowser, isSafariBrowser } = useMemo(() => getBrowserAudioRoutingSupport(), []);
  const isLimitedAudioOutputBrowser = isIOSBrowser || isSafariBrowser;
  const canUseAudioOutputSelection = canSelectAudioOutput && !isLimitedAudioOutputBrowser;
  const audioOutputSupportMessage = !canSelectAudioOutput
    ? 'Speaker selection is not supported in this browser.'
    : isLimitedAudioOutputBrowser
      ? 'Speaker and earpiece switching is limited on iOS/Safari browsers. Use the device audio controls if the route does not change.'
      : null;

  useEffect(() => {
    isMicOnRef.current = isMicOn;
  }, [isMicOn]);

  useEffect(() => {
    isVideoOnRef.current = isVideoOn;
  }, [isVideoOn]);

  useEffect(() => {
    speakerPreferenceRef.current = speakerPreference;
  }, [speakerPreference]);

  const getSubscriberVideoElement = useCallback(
    () => subscriberContainerRef.current?.querySelector('video') ?? null,
    []
  );
  const isMobileConnectedCall = isCallConnected && isMobileViewport;

  const updateRemotePortraitState = useCallback(() => {
    const videoElement = getSubscriberVideoElement();
    if (!videoElement) return;

    const width = videoElement.videoWidth;
    const height = videoElement.videoHeight;
    if (!width || !height) return;

    const ratio = height / width;
    // Treat significantly-taller-than-wide streams as "mobile portrait".
    // Threshold avoids minor fluctuations and square-ish streams.
    setIsRemotePortrait(ratio >= 1.15);
  }, [getSubscriberVideoElement]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileViewport(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!callData?.callId) return undefined;

    let currentVideo = null;
    const bindVideo = () => {
      const nextVideo = getSubscriberVideoElement();
      if (!nextVideo || nextVideo === currentVideo) return;

      if (currentVideo) {
        currentVideo.removeEventListener('loadedmetadata', updateRemotePortraitState);
        currentVideo.removeEventListener('resize', updateRemotePortraitState);
      }

      currentVideo = nextVideo;
      currentVideo.addEventListener('loadedmetadata', updateRemotePortraitState);
      currentVideo.addEventListener('resize', updateRemotePortraitState);
      updateRemotePortraitState();
    };

    bindVideo();
    const observer = new MutationObserver(bindVideo);
    if (subscriberContainerRef.current) {
      observer.observe(subscriberContainerRef.current, { childList: true, subtree: true });
    }

    return () => {
      observer.disconnect();
      if (currentVideo) {
        currentVideo.removeEventListener('loadedmetadata', updateRemotePortraitState);
        currentVideo.removeEventListener('resize', updateRemotePortraitState);
      }
    };
  }, [callData?.callId, getSubscriberVideoElement, updateRemotePortraitState]);

  const stopMediaStreamTracks = useCallback((stream) => {
    if (!stream) return;
    stream.getTracks().forEach((track) => track.stop());
  }, []);

  const applySpeakerOutputPreference = useCallback(async (preference, { silent = false } = {}) => {
    if (!preference) return false;
    if (!canUseAudioOutputSelection) {
      if (!silent) {
        setMediaNotice(audioOutputSupportMessage || 'Speaker selection is not supported in this browser.');
      }
      return false;
    }

    const sinkId =
      !preference.deviceId || preference.deviceId === DEFAULT_SPEAKER_ID
        ? DEFAULT_SPEAKER_ID
        : preference.deviceId;

    try {
      const livekitSession = livekitSessionRef.current;
      if (livekitSession) {
        await livekitSession.setAudioOutputDevice(sinkId);
        return true;
      }
      await OT.setAudioOutputDevice(sinkId);
      return true;
    } catch (error) {
      if (!silent) {
        setMediaNotice('Unable to route call audio to the selected speaker.');
      }
      return false;
    }
  }, [audioOutputSupportMessage, canUseAudioOutputSelection]);

  const clearPublisherContainer = useCallback(() => {
    if (publisherContainerRef.current) {
      publisherContainerRef.current.innerHTML = '';
    }
  }, []);

  const syncPublisherPreviewContainer = useCallback(() => {
    const container = publisherContainerRef.current;
    const publisher = publisherRef.current;

    // The self-view container is rendered in two places (mobile / desktop), so
    // the ref can point at a different node than the one publish() attached to.
    // LiveKit can just re-attach the local track into whichever is current.
    if (publisher?.isLiveKit && container && livekitSessionRef.current) {
      if (!publisher.element || !container.contains(publisher.element)) {
        publisher.element = livekitSessionRef.current.attachLocalPreview(container);
      }
      return;
    }

    const publisherElement = publisher?.element;

    if (!container || !publisherElement) return;
    if (container.contains(publisherElement)) return;

    container.innerHTML = '';
    container.appendChild(publisherElement);
  }, []);

  const stopManagedAudioStream = useCallback(() => {
    if (!managedAudioStreamRef.current) return;

    stopMediaStreamTracks(managedAudioStreamRef.current);
    managedAudioStreamRef.current = null;
  }, [stopMediaStreamTracks]);

  const requestMicrophonePermission = useCallback(async () => {
    if (isMeditalk) {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stopMediaStreamTracks(stream);
      return;
    }
    const stream = await OT.getUserMedia({
      audioSource: true,
      videoSource: null,
    });
    stopMediaStreamTracks(stream);
  }, [isMeditalk, stopMediaStreamTracks]);

  const requestCameraPermission = useCallback(async () => {
    if (isMeditalk) {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stopMediaStreamTracks(stream);
      return;
    }
    const stream = await OT.getUserMedia({
      audioSource: false,
      videoSource: true,
    });
    stopMediaStreamTracks(stream);
  }, [isMeditalk, stopMediaStreamTracks]);

  const listMicrophoneDevices = useCallback(() => {
    if (isMeditalk) return listLiveKitDevices('audioinput');
    return (
    new Promise((resolve, reject) => {
      OT.getDevices((error, devices) => {
        if (error) {
          reject(error);
          return;
        }

        resolve(
          (devices || [])
            .filter((device) => device.kind === 'audioInput')
            .map((device) => ({
              kind: 'audioinput',
              deviceId: device.deviceId,
              label: device.label || 'Microphone',
            }))
        );
      });
    })
    );
  }, [isMeditalk]);

  const listSpeakerDevices = useCallback(async () => {
    if (!canUseAudioOutputSelection) {
      return [];
    }

    if (isMeditalk) return listLiveKitDevices('audiooutput');

    const devices = await OT.getAudioOutputDevices();
    return (devices || []).map((device) => ({
      kind: 'audiooutput',
      deviceId: device.deviceId || DEFAULT_SPEAKER_ID,
      label: device.label || 'Default speaker',
    }));
  }, [canUseAudioOutputSelection, isMeditalk]);

  const listCameraDevices = useCallback(() => {
    if (isMeditalk) return listLiveKitDevices('videoinput');
    return (
    new Promise((resolve, reject) => {
      OT.getDevices((error, devices) => {
        if (error) {
          reject(error);
          return;
        }

        resolve(
          (devices || [])
            .filter((device) => device.kind === 'videoInput')
            .map((device) => ({
              kind: 'videoinput',
              deviceId: device.deviceId,
              label: device.label || 'Camera',
            }))
        );
      });
    })
    );
  }, [isMeditalk]);

  const buildPublisherProperties = useCallback((audioSourceId, videoSourceId, streamSource = null) => {
    const properties = {
      insertMode: 'append',
      width: '100%',
      height: '100%',
      fitMode: 'cover',
      showControls: false,
      ...RELIABLE_VIDEO_PUBLISHER_OPTIONS,
      publishAudio: isMicOnRef.current,
      publishVideo: isVideoOnRef.current,
    };

    // A pre-recorded clip supplies its own tracks, so the device ids below are
    // irrelevant — Vonage publishes exactly what it is handed.
    if (streamSource) {
      properties.videoSource = streamSource.videoTrack;
      if (streamSource.audioTrack) {
        properties.audioSource = streamSource.audioTrack;
      }
      return properties;
    }

    if (audioSourceId && audioSourceId !== DEFAULT_MICROPHONE_ID) {
      properties.audioSource = audioSourceId;
    }

    if (videoSourceId) {
      properties.videoSource = videoSourceId;
    }

    return properties;
  }, []);

  const initPublisherInstance = useCallback((audioSourceId, videoSourceId, onMediaIssue, streamSource = null) => (
    new Promise((resolve, reject) => {
      if (!publisherContainerRef.current) {
        reject(new Error('Publisher container is not available.'));
        return;
      }

      clearPublisherContainer();
      const nextPublisher = OT.initPublisher(
        publisherContainerRef.current,
        buildPublisherProperties(audioSourceId, videoSourceId, streamSource),
        (error) => {
          if (error) {
            if (isIgnorableVonageIssue(error)) {
              onMediaIssue?.('Unable to start your camera preview right now. Remote video can still continue.');
            }
            reject(error);
            return;
          }

          applyPublisherNetworkProfile(nextPublisher);
          resolve(nextPublisher);
        }
      );
    })
  ), [buildPublisherProperties, clearPublisherContainer]);

  const publishPublisherToSession = useCallback((session, publisher) => (
    new Promise((resolve, reject) => {
      session.publish(publisher, (error) => {
        if (error) {
          reject(error);
          return;
        }
        resolve();
      });
    })
  ), []);

  const captureAndUploadScreenshot = useCallback(async () => {
    if (!callData?.callId || screenshotUploadInFlightRef.current) return;

    const videoElement = getSubscriberVideoElement();
    if (!videoElement || videoElement.readyState < 2 || !videoElement.videoWidth || !videoElement.videoHeight) {
      return;
    }

    const canvas = screenshotCanvasRef.current ?? document.createElement('canvas');
    screenshotCanvasRef.current = canvas;
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

    screenshotUploadInFlightRef.current = true;
    try {
      const imageBlob = await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', 0.8);
      });

      if (!imageBlob) return;
      await savePortalDeliveryVideoCallImage(callData.callId, imageBlob);
    } catch (error) {
      console.error('Error saving delivery video call screenshot:', error);
    } finally {
      screenshotUploadInFlightRef.current = false;
    }
  }, [callData?.callId, getSubscriberVideoElement]);

  const requestRemotePictureInPicture = useCallback(async () => {
    if (!canUsePictureInPicture) return false;

    const videoElement = getSubscriberVideoElement();
    if (!videoElement || typeof videoElement.requestPictureInPicture !== 'function') {
      return false;
    }

    if (document.pictureInPictureElement === videoElement) {
      return true;
    }

    try {
      await videoElement.requestPictureInPicture();
      return true;
    } catch (error) {
      return false;
    }
  }, [canUsePictureInPicture, getSubscriberVideoElement]);

  const loadActiveMedicationData = useCallback(async (patientId) => {
    if (!patientId) return;

    const pharmacyDate = getTodayInPharmacyTimezone();
    setIsMedicationListLoading(true);
    setRawActiveMedications([]);
    setDailyPrescriptions([]);

    try {
      let resolvedPrescriptionList = [];

      try {
        const prescriptionsResult = await fetchPortalPatientPrescriptionDetails(patientId, pharmacyDate);
        resolvedPrescriptionList = getPrescriptionList(prescriptionsResult);
        setDailyPrescriptions(resolvedPrescriptionList);
      } catch (error) {
        console.error('Error loading portal patient prescription details:', error);
        setDailyPrescriptions([]);
      }

      if (!hasListData(resolvedPrescriptionList)) {
        const medsResult = await fetchPortalPatientActiveMedications(patientId);
        setRawActiveMedications(getMedicationList(medsResult));
      }
    } catch (error) {
      console.error('Error loading portal patient medications:', error);
      setRawActiveMedications([]);
      setDailyPrescriptions([]);
    } finally {
      setIsMedicationListLoading(false);
    }
  }, []);

  const loadAllMedicationData = useCallback(async (patientId) => {
    if (!patientId) return;

    setIsMedicationListLoading(true);
    setRawActiveMedications([]);
    setDailyPrescriptions([]);

    try {
      const allRxResponse = await fetchPortalPatientAllRx(patientId, 1);
      const prescriptions =
        allRxResponse?.prescriptions ??
        allRxResponse?.data?.prescriptions ??
        [];

      setRawActiveMedications(Array.isArray(prescriptions) ? prescriptions : []);
    } finally {
      setIsMedicationListLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!callData?.patientId) {
      setIsPatientDataLoading(false);
      setIsMedicationListLoading(false);
      return undefined;
    }

    setIsPatientDataLoading(true);
    setIsMedicationListLoading(true);
    setRawActiveMedications([]);
    setDailyPrescriptions([]);

    const loadPatientData = async () => {
      try {
        const [profileResult] = await Promise.allSettled([
          fetchPortalPatientProfile(callData.patientId),
        ]);

        if (cancelled) return;

        if (profileResult.status === 'fulfilled') {
          setPatientProfile(getPatientProfileData(profileResult.value));
        }

        await loadActiveMedicationData(callData.patientId);
      } finally {
        if (!cancelled) {
          setIsPatientDataLoading(false);
          setIsMedicationListLoading(false);
        }
      }
    };

    loadPatientData();

    return () => {
      cancelled = true;
    };
  }, [callData?.patientId, loadActiveMedicationData]);

  useEffect(() => {
    if (!callData?.callId) return undefined;

    const tick = () => setCallDateTime(getPharmacyCurrentDateTimeParts());
    tick();
    const interval = window.setInterval(tick, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [callData?.callId]);

  // ------------------------------------------------------------ MediTalk call
  // LiveKit path. Runs only when the backend pinned this call to MediTalk; the
  // Vonage effect below returns early for those, so exactly one of the two ever
  // owns a session.
  useEffect(() => {
    if (!callData?.callId || !isMeditalk) return undefined;

    if (!hasLiveKitCredentials(callData)) {
      setConnectionError('This call could not be set up (missing video session details). Please end the call and try again.');
      setIsCallConnected(false);
      return undefined;
    }

    const session = createLiveKitSession(callData);
    if (!session) return undefined;

    let isCancelled = false;
    const subscriberContainer = subscriberContainerRef.current;
    livekitSessionRef.current = session;
    setPublisherInstance(null);
    setConnectionError(null);
    setMediaNotice(null);
    setIsRemoteCameraOff(false);
    setIsRemoteCameraPermissionDenied(false);
    setIsCallConnected(false);

    const handleMediaIssue = (message) => {
      if (!isCancelled) setMediaNotice(message);
    };
    const remoteName = callData?.driverName || 'The other person';

    session.on('streamCreated', ({ stream }) => {
      if (isCancelled || !subscriberContainer) return;
      setIsRemoteCameraPermissionDenied(false);
      setIsRemoteCameraOff(false);
      session.attach(stream.track, subscriberContainer);
      applySpeakerOutputPreference(speakerPreferenceRef.current, { silent: true }).catch(() => {});
    });

    // LiveKit reports "their camera went away" as a mute on the publication,
    // which is the same user-visible situation Vonage's videoDisabled covered.
    session.on('videoDisabled', () => {
      if (isCancelled) return;
      setIsRemoteCameraOff(true);
      handleMediaIssue(`${remoteName} turned their camera off.`);
    });
    session.on('videoEnabled', () => {
      if (isCancelled) return;
      setIsRemoteCameraOff(false);
      setIsRemoteCameraPermissionDenied(false);
      setMediaNotice(null);
    });

    // Reconnects must look like reconnects, not like a dead call.
    session.on('reconnecting', () => handleMediaIssue('Reconnecting to the call...'));
    session.on('reconnected', () => {
      if (!isCancelled) setMediaNotice(null);
    });
    // Post-grace-period only. A raw participantDisconnected fires on ordinary
    // reconnects and must never be treated as the call ending.
    session.on('remoteLeft', () => {
      if (isCancelled) return;
      handleMediaIssue(`${remoteName} has left the call.`);
    });

    session.on('autoplayBlocked', () => {
      if (isCancelled) return;
      setIsCallAudioBlocked(true);
      handleMediaIssue('Tap "Enable sound" to hear the other participant.');
    });

    // A device that failed to open is not a failed call — publish() carries on
    // with whatever it did get, so this only needs to be reported.
    session.on('mediaError', ({ error }) => {
      if (isCancelled) return;
      if (isPermissionDeniedIssue(error, true)) {
        handleMediaIssue('Camera or microphone is blocked. You can still see and hear the other participant — enable permissions to share your video.');
        return;
      }
      handleMediaIssue('One of your devices could not be started. The call can continue.');
    });

    // Refresh: React cleanup does not run on unload, and LiveKit would hold the
    // participant slot until its own timeout, leaving the pharmacist's line
    // busy. Leave the room explicitly, as the Vonage SDK used to.
    const leaveRoom = () => {
      try {
        session.disconnect();
      } catch (error) { }
    };
    window.addEventListener('beforeunload', leaveRoom);
    window.addEventListener('pagehide', leaveRoom);

    const storedMicrophonePreference = readStoredMicrophonePreference();
    const storedCameraPreference = readStoredCameraPreference();
    const initialAudioSourceId =
      storedMicrophonePreference?.deviceId && storedMicrophonePreference.deviceId !== DEFAULT_MICROPHONE_ID
        ? storedMicrophonePreference.deviceId
        : undefined;
    const initialVideoSourceId =
      storedCameraPreference?.deviceId && storedCameraPreference.deviceId !== DEFAULT_CAMERA_ID
        ? storedCameraPreference.deviceId
        : undefined;

    // A shim wearing the two Vonage publisher methods the rest of this
    // component calls, so the device pickers, mute toggles and preview sync all
    // keep working without a provider check at each call site.
    const buildPublisherShim = () => ({
      isLiveKit: true,
      element: null,
      publishAudio: (enabled) => { session.setMicrophoneEnabled(enabled).catch(() => {}); },
      publishVideo: (enabled) => { session.setCameraEnabled(enabled).catch(() => {}); },
      destroy: () => {},
    });

    (async () => {
      let streamSource = null;
      try {
        await session.connect(callData.vonage_token);
        if (isCancelled) {
          leaveRoom();
          return;
        }
        setIsCallConnected(true);

        // A pre-recorded clip replaces the camera/mic when this pharmacist is
        // set up for one. Its raw MediaStreamTracks go straight to LiveKit.
        // Any failure falls through to the real devices so a bad clip can never
        // block the call.
        if (usesStreamSource) {
          try {
            streamSource = await createDeliveryCallStreamSource(callData.video_stream_url);
            streamSourceRef.current = streamSource;
          } catch (streamError) {
            console.error('Falling back to the camera for this delivery call:', streamError);
            streamSourceRef.current?.stop();
            streamSourceRef.current = null;
            streamSource = null;
            handleMediaIssue('The pre-recorded clip could not start, so your camera is being used instead.');
          }
        }

        if (isCancelled) {
          streamSourceRef.current?.stop();
          streamSourceRef.current = null;
          leaveRoom();
          return;
        }

        await session.publish({
          role: 'pharmacist',
          streamSource,
          audioSourceId: initialAudioSourceId,
          videoSourceId: initialVideoSourceId,
          publishAudio: isMicOnRef.current,
          publishVideo: isVideoOnRef.current,
        });
        if (isCancelled) return;

        const shim = buildPublisherShim();
        shim.element = session.attachLocalPreview(publisherContainerRef.current);
        publisherRef.current = shim;
        setPublisherInstance(shim);
      } catch (error) {
        if (isCancelled) return;
        if (isPermissionDeniedIssue(error, true)) {
          handleMediaIssue('Camera or microphone is blocked. You can still see and hear the other participant — enable permissions to share your video.');
          return;
        }
        if (isIgnorableVonageIssue(error, true)) {
          handleMediaIssue('Local camera publishing was interrupted. The call stays open and the remote video can continue.');
          return;
        }
        setConnectionError('Unable to connect the video call.');
        setIsCallConnected(false);
      }
    })();

    return () => {
      isCancelled = true;
      window.removeEventListener('beforeunload', leaveRoom);
      window.removeEventListener('pagehide', leaveRoom);
      stopManagedAudioStream();
      try {
        streamSourceRef.current?.stop();
      } catch (error) { }
      streamSourceRef.current = null;
      leaveRoom();
      livekitSessionRef.current = null;
      publisherRef.current = null;
      setPublisherInstance(null);
      clearPublisherContainer();
      if (subscriberContainer) subscriberContainer.innerHTML = '';
      setIsCallConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applySpeakerOutputPreference, callData, clearPublisherContainer, isMeditalk, stopManagedAudioStream, usesStreamSource]);

  // -------------------------------------------------------------- Vonage call
  useEffect(() => {
    if (!callData?.callId) return undefined;
    if (isMeditalk) return undefined;

    // The Vonage credentials only arrive on the accept response. If any are
    // missing, surface it instead of sitting on an infinite "connecting" spinner
    // with no diagnostics.
    if (!callData?.vonage_session_id || !callData?.vonage_token || !callData?.application_id) {
      setConnectionError('This call could not be set up (missing video session details). Please end the call and try again.');
      setIsCallConnected(false);
      return undefined;
    }

    let isCancelled = false;
    let canPublishToSession = true;
    let preparedPublisher = null;
    setPublisherInstance(null);
    setConnectionError(null);
    setMediaNotice(null);
    setIsRemoteCameraOff(false);
    setIsRemoteCameraPermissionDenied(false);
    setIsCallConnected(false);
    const handleMediaIssue = (message) => {
      if (!isCancelled) {
        setMediaNotice(message);
      }
    };
    const handleAutoplayBlocked = () => {
      if (isCancelled) return;
      setIsCallAudioBlocked(true);
      handleMediaIssue('Tap "Enable sound" to hear the other participant.');
    };
    const handleVonageException = (event) => {
      if (isAutoplayBlockedIssue(event)) {
        handleAutoplayBlocked();
        event?.preventDefault?.();
        return;
      }
      if (isIgnorableVonageIssue(event)) {
        handleMediaIssue('Camera preview paused. The call can continue and picture-in-picture is available for the remote video.');
        event?.preventDefault?.();
        return;
      }

      if (event?.message && !isCancelled) {
        setConnectionError('Unable to keep the video call connected.');
      }
    };
    const handleWindowError = (event) => {
      const errorValue = event?.error || event?.message || event;
      if (isAutoplayBlockedIssue(errorValue)) {
        handleAutoplayBlocked();
        event?.preventDefault?.();
        event?.stopImmediatePropagation?.();
        return;
      }
      if (isIgnorableVonageIssue(errorValue)) {
        handleMediaIssue('Camera preview paused. The call can continue and picture-in-picture is available for the remote video.');
        event?.preventDefault?.();
        event?.stopImmediatePropagation?.();
      }
    };
    const handleUnhandledRejection = (event) => {
      if (isAutoplayBlockedIssue(event?.reason)) {
        handleAutoplayBlocked();
        event?.preventDefault?.();
        return;
      }
      if (isIgnorableVonageIssue(event?.reason)) {
        handleMediaIssue('Camera preview paused. The call can continue and picture-in-picture is available for the remote video.');
        event?.preventDefault?.();
      }
    };

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    if (typeof OT.on === 'function') {
      OT.on('exception', handleVonageException);
    }
    const session = OT.initSession(callData.application_id, callData.vonage_session_id);
    session.on('exception', handleVonageException);
    session.on('signal:cameraPermissionDenied', () => {
      if (isCancelled) return;
      const remoteName = callData?.driverName || 'The driver';
      setIsRemoteCameraOff(false);
      setIsRemoteCameraPermissionDenied(true);
      handleMediaIssue(`${remoteName} has not allowed camera permission. You can still continue the call with audio.`);
    });

    session.on('streamCreated', (event) => {
      if (isCancelled || !subscriberContainerRef.current) return;
      setIsRemoteCameraPermissionDenied(false);
      const subscriber = session.subscribe(
        event.stream,
        subscriberContainerRef.current,
        {
          insertMode: 'append',
          width: '100%',
          height: '100%',
          fitMode: 'cover',
          showControls: false,
        },
        (error) => {
          if (error && !isCancelled) {
            if (isIgnorableVonageIssue(error)) {
              handleMediaIssue('Remote video is reconnecting.');
              return;
            }
            setConnectionError('Unable to load the other participant video.');
            return;
          }

          applySpeakerOutputPreference(speakerPreferenceRef.current, { silent: true }).catch(() => {});
        }
      );
      subscriberRef.current = subscriber;

      // The OpenTok media server drops the remote video when the *other* side's
      // connection is weak (event reason "quality") or when they turn their camera
      // off ("publishVideo"). Unhandled, the pharmacist just sees a black box with
      // audio and no explanation — this is one of the "blank/black screen, no video"
      // reports, and the "takes forever to load the other person" complaint (video
      // is suppressed until bandwidth recovers, then re-enabled automatically).
      // Surface it as a transient notice and clear it the instant video returns.
      if (subscriber && typeof subscriber.on === 'function') {
        subscriber.on('videoDisabled', (disabledEvent) => {
          if (isCancelled) return;
          const reason = disabledEvent?.reason;
          const remoteName = callData?.driverName || 'The other person';
          if (reason === 'quality') {
            handleMediaIssue(
              `${remoteName}'s video paused because their internet is weak right now. You can still hear them, and their video will come back on its own.`
            );
          } else if (reason === 'publishVideo') {
            setIsRemoteCameraOff(true);
            handleMediaIssue(`${remoteName} turned their camera off.`);
          }
        });
        subscriber.on('videoEnabled', () => {
          if (isCancelled) return;
          setIsRemoteCameraOff(false);
          setIsRemoteCameraPermissionDenied(false);
          setMediaNotice(null);
        });
      }
    });

    const storedMicrophonePreference = readStoredMicrophonePreference();
    const storedCameraPreference = readStoredCameraPreference();
    const initialAudioSourceId = storedMicrophonePreference?.deviceId || DEFAULT_MICROPHONE_ID;
    const initialVideoSourceId =
      storedCameraPreference?.deviceId && storedCameraPreference.deviceId !== DEFAULT_CAMERA_ID
        ? storedCameraPreference.deviceId
        : undefined;

    const preparePublisher = (audioSourceId, videoSourceId, streamSource = null) =>
      initPublisherInstance(audioSourceId, videoSourceId, handleMediaIssue, streamSource)
        .then((publisher) => {
          preparedPublisher = publisher;
          if (isCancelled || !canPublishToSession) {
            try {
              publisher.destroy();
            } catch (destroyError) { }
            preparedPublisher = null;
            return null;
          }

          return publisher;
        });

    // When this pharmacist is set up to publish a pre-recorded clip, use it in
    // place of the camera/mic. Any failure falls through to the real devices so
    // a bad or missing clip can never block the call.
    const prepareStreamPublisher = async () => {
      try {
        const streamSource = await createDeliveryCallStreamSource(callData.video_stream_url);
        streamSourceRef.current = streamSource;

        if (isCancelled || !canPublishToSession) {
          streamSource.stop();
          streamSourceRef.current = null;
          return null;
        }

        return await preparePublisher(null, null, streamSource);
      } catch (streamError) {
        console.error('Falling back to the camera for this delivery call:', streamError);
        streamSourceRef.current?.stop();
        streamSourceRef.current = null;
        handleMediaIssue('The pre-recorded clip could not start, so your camera is being used instead.');
        return preparePublisher(initialAudioSourceId, initialVideoSourceId);
      }
    };

    const preparedPublisherPromise = (
      usesStreamSource
        ? prepareStreamPublisher()
        : preparePublisher(initialAudioSourceId, initialVideoSourceId)
    )
      .catch((publishError) => {
        if (isCancelled) throw publishError;
        if (!usesStreamSource && (initialAudioSourceId !== DEFAULT_MICROPHONE_ID || initialVideoSourceId)) {
          return preparePublisher(DEFAULT_MICROPHONE_ID, undefined);
        }
        throw publishError;
      });

    // Attach a no-op catch synchronously to prevent unhandled promise rejections
    // if the promise rejects before the session.connect callback runs.
    preparedPublisherPromise.catch(() => {});

    session.connect(callData.vonage_token, (error) => {
      if (isCancelled) return;
      if (error) {
        canPublishToSession = false;
        if (preparedPublisher) {
          try {
            preparedPublisher.destroy();
          } catch (destroyError) { }
          preparedPublisher = null;
        }
        setConnectionError('Unable to connect the video call.');
        setIsCallConnected(false);
        return;
      }

      preparedPublisherPromise
        .then((publisher) => {
          if (!publisher || isCancelled || !canPublishToSession) {
            return;
          }

          return publishPublisherToSession(session, publisher)
            .then(() => {
              if (isCancelled) {
                try {
                  publisher.destroy();
                } catch (destroyError) { }
                return;
              }

              publisherRef.current = publisher;
              preparedPublisher = publisher;
              setPublisherInstance(publisher);
            });
        })
        .catch((publishError) => {
          if (isCancelled) return;
          if (isPermissionDeniedIssue(publishError)) {
            // Camera/mic blocked: stay connected so the pharmacist can still see
            // and hear the patient, rather than failing the whole call.
            handleMediaIssue('Camera or microphone is blocked. You can still see and hear the other participant — enable permissions to share your video.');
            return;
          }
          if (isIgnorableVonageIssue(publishError)) {
            handleMediaIssue('Local camera publishing was interrupted. The call stays open and the remote video can continue.');
            return;
          }
          setConnectionError('Connected, but your video could not be published.');

          setIsCallConnected(false);
          return;
        });
      setIsCallConnected(true);
    });



    return () => {
      isCancelled = true;
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      if (typeof OT.off === 'function') {
        OT.off('exception', handleVonageException);
      }
      try {
        if (subscriberRef.current) {
          session.unsubscribe(subscriberRef.current);
          subscriberRef.current = null;
        }
      } catch (error) { }
      try {
        const publishedPublisher = publisherRef.current;
        if (publishedPublisher) {
          publishedPublisher.destroy();
          publisherRef.current = null;
        }
        if (preparedPublisher && preparedPublisher !== publishedPublisher) {
          preparedPublisher.destroy();
          preparedPublisher = null;
        }
      } catch (error) { }
      stopManagedAudioStream();
      try {
        streamSourceRef.current?.stop();
      } catch (error) { }
      streamSourceRef.current = null;
      clearPublisherContainer();
      try {
        session.disconnect();
      } catch (error) { }
      setIsCallConnected(false);
    };
  }, [applySpeakerOutputPreference, callData, clearPublisherContainer, initPublisherInstance, isMeditalk, publishPublisherToSession, stopManagedAudioStream]);

  useEffect(() => {
    if (!mediaNotice) return undefined;
    if (isRemoteCameraOff || isRemoteCameraPermissionDenied) return undefined;

    const timeoutId = window.setTimeout(() => {
      setMediaNotice(null);
    }, 4500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isRemoteCameraOff, isRemoteCameraPermissionDenied, mediaNotice]);

  useEffect(() => {
    if (!callData?.callId || !canUsePictureInPicture || !subscriberContainerRef.current) {
      setIsPiPSupported(false);
      return undefined;
    }

    let currentVideo = null;
    const handleEnter = () => setIsPiPActive(true);
    const handleLeave = () => setIsPiPActive(false);
    const bindVideo = () => {
      const nextVideo = getSubscriberVideoElement();
      if (nextVideo === currentVideo) return;

      if (currentVideo) {
        currentVideo.removeEventListener('enterpictureinpicture', handleEnter);
        currentVideo.removeEventListener('leavepictureinpicture', handleLeave);
      }

      currentVideo = nextVideo;
      setIsPiPSupported(Boolean(currentVideo && typeof currentVideo.requestPictureInPicture === 'function'));

      if (currentVideo) {
        currentVideo.addEventListener('enterpictureinpicture', handleEnter);
        currentVideo.addEventListener('leavepictureinpicture', handleLeave);
      }
    };

    bindVideo();
    const observer = new MutationObserver(bindVideo);
    observer.observe(subscriberContainerRef.current, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (currentVideo) {
        currentVideo.removeEventListener('enterpictureinpicture', handleEnter);
        currentVideo.removeEventListener('leavepictureinpicture', handleLeave);
      }
    };
  }, [callData?.callId, canUsePictureInPicture, getSubscriberVideoElement]);

  useEffect(() => {
    if (!callData?.callId || !canUseAudioOutputSelection) {
      return undefined;
    }

    let cancelled = false;

    applySpeakerOutputPreference(speakerPreferenceRef.current, { silent: true })
      .catch(() => {})
      .finally(() => {
        if (cancelled) return;
      });

    return () => {
      cancelled = true;
    };
  }, [applySpeakerOutputPreference, callData?.callId, canUseAudioOutputSelection]);

  useEffect(() => {
    if (!callData?.callId || !canUseAudioOutputSelection) {
      return undefined;
    }

    const reapplySpeakerPreference = () => {
      applySpeakerOutputPreference(speakerPreferenceRef.current, { silent: true }).catch(() => {});
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        reapplySpeakerPreference();
      }
    };

    window.addEventListener('focus', reapplySpeakerPreference);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', reapplySpeakerPreference);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [applySpeakerOutputPreference, callData?.callId, canUseAudioOutputSelection]);

  useEffect(() => {
    let animationFrameId = null;

    const syncPreview = () => {
      syncPublisherPreviewContainer();
    };

    syncPreview();
    animationFrameId = window.requestAnimationFrame(syncPreview);

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isRemotePortrait, publisherInstance, syncPublisherPreviewContainer]);

  useEffect(() => {
    if (!callData?.callId || connectionError) return undefined;

    const startCaptureLoop = () => {
      if (screenshotIntervalRef.current) return;
      screenshotIntervalRef.current = window.setInterval(() => {
        captureAndUploadScreenshot();
      }, SCREENSHOT_CAPTURE_INTERVAL_MS);
      captureAndUploadScreenshot();
    };

    const stopCaptureLoop = () => {
      if (screenshotIntervalRef.current) {
        clearInterval(screenshotIntervalRef.current);
        screenshotIntervalRef.current = null;
      }
      screenshotUploadInFlightRef.current = false;
    };

    const videoElement = getSubscriberVideoElement();
    const handleVideoReady = () => startCaptureLoop();

    if (videoElement) {
      if (videoElement.readyState >= 2) {
        startCaptureLoop();
      } else {
        videoElement.addEventListener('loadeddata', handleVideoReady);
        videoElement.addEventListener('playing', handleVideoReady);
      }
    }

    const observer = new MutationObserver(() => {
      const nextVideoElement = getSubscriberVideoElement();
      if (!nextVideoElement) return;

      if (nextVideoElement.readyState >= 2) {
        startCaptureLoop();
        return;
      }

      nextVideoElement.addEventListener('loadeddata', handleVideoReady, { once: true });
      nextVideoElement.addEventListener('playing', handleVideoReady, { once: true });
    });

    if (subscriberContainerRef.current) {
      observer.observe(subscriberContainerRef.current, { childList: true, subtree: true });
    }

    return () => {
      observer.disconnect();
      if (videoElement) {
        videoElement.removeEventListener('loadeddata', handleVideoReady);
        videoElement.removeEventListener('playing', handleVideoReady);
      }
      stopCaptureLoop();
    };
  }, [callData?.callId, captureAndUploadScreenshot, connectionError, getSubscriberVideoElement]);

  useEffect(() => {
    if (!callData?.callId || !canUsePictureInPicture) return undefined;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        requestRemotePictureInPicture();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [callData?.callId, canUsePictureInPicture, requestRemotePictureInPicture]);

  useEffect(() => {
    const currentPublisher = publisherRef.current;
    if (!currentPublisher) return;

    try {
      currentPublisher.publishAudio(isMicOn);
    } catch (error) {
      setMediaNotice(`Unable to ${isMicOn ? 'unmute' : 'mute'} the microphone right now.`);
    }
  }, [isMicOn, publisherInstance]);

  useEffect(() => {
    const currentPublisher = publisherRef.current;
    if (!currentPublisher) return;

    try {
      currentPublisher.publishVideo(isVideoOn);
    } catch (error) {
      setMediaNotice(`Unable to turn ${isVideoOn ? 'on' : 'off'} the camera right now.`);
    }
  }, [isVideoOn, publisherInstance]);

  useEffect(() => {
    setPatientVerification(
      normalizePatientVerificationPayload(
        callData?.patient_verification,
        callData?.verified_stamp ?? callData?.patient_verification?.verified_stamp ?? ''
      )
    );
    setVerifiedStamp(callData?.verified_stamp ?? callData?.patient_verification?.verified_stamp ?? '');
  }, [callData?.patient_verification, callData?.verified_stamp]);

  const toggleMic = () => {
    setIsMicOn((previous) => !previous);
  };

  const toggleVideo = () => {
    setIsVideoOn((previous) => !previous);
  };

  const handleSelectMicrophone = useCallback(async (microphoneOption) => {
    const currentPublisher = publisherRef.current;

    if (!currentPublisher || !microphoneOption) {
      return false;
    }

    const requestedAudioSourceId = microphoneOption.deviceId || DEFAULT_MICROPHONE_ID;
    let nextAudioStream = null;

    const livekitSession = livekitSessionRef.current;
    if (livekitSession) {
      // restartTrack swaps the device on the existing publication, so the
      // remote side never sees the track go away.
      try {
        await livekitSession.setAudioSource(
          requestedAudioSourceId === DEFAULT_MICROPHONE_ID ? undefined : requestedAudioSourceId
        );
        await livekitSession.setMicrophoneEnabled(isMicOnRef.current);
        return true;
      } catch (error) {
        return false;
      }
    }

    try {
      if (requestedAudioSourceId === DEFAULT_MICROPHONE_ID) {
        nextAudioStream = await OT.getUserMedia({
          audioSource: true,
          videoSource: null,
        });

        const nextAudioTrack = nextAudioStream.getAudioTracks()[0];
        if (!nextAudioTrack) {
          throw new Error('No microphone track available.');
        }

        await currentPublisher.setAudioSource(nextAudioTrack);
      } else {
        await currentPublisher.setAudioSource(requestedAudioSourceId);
      }

      currentPublisher.publishAudio(isMicOnRef.current);
      stopManagedAudioStream();
      managedAudioStreamRef.current = nextAudioStream;
      return true;
    } catch (error) {
      stopMediaStreamTracks(nextAudioStream);
      return false;
    }
  }, [stopManagedAudioStream, stopMediaStreamTracks]);

  // Switches a clip-publishing call over to the pharmacist's real camera and
  // microphone for the remainder of the call. The publisher stays the same
  // (so the driver's subscription is uninterrupted); only its sources change.
  // User-gesture unlock for remote audio the browser blocked from autoplaying.
  const handleEnableCallAudio = useCallback(async () => {
    const videoElement = getSubscriberVideoElement();
    try {
      // LiveKit delivers remote audio on its own <audio> elements, not on the
      // subscriber video, so unmuting the video alone would not help. This is
      // the gesture LiveKit asks for.
      await livekitSessionRef.current?.startAudio();
      if (videoElement) {
        videoElement.muted = false;
        await videoElement.play();
      }
      setIsCallAudioBlocked(false);
      setMediaNotice(null);
    } catch (error) {
      // Still blocked — leave the prompt up so they can try again.
    }
  }, [getSubscriberVideoElement]);

  const handleSwitchToLiveVideo = useCallback(async () => {
    const currentPublisher = publisherRef.current;
    if (!currentPublisher || isSwitchingToLiveVideo || hasSwitchedToLiveVideo) {
      return false;
    }

    setIsSwitchingToLiveVideo(true);
    let nextAudioStream = null;

    const livekitSession = livekitSessionRef.current;
    if (livekitSession) {
      try {
        // Same publication, new sources — the driver's subscription is never
        // interrupted, exactly as on the Vonage path.
        await livekitSession.setVideoSource(undefined);
        await livekitSession.setAudioSource(undefined);
        await livekitSession.setCameraEnabled(isVideoOnRef.current);
        await livekitSession.setMicrophoneEnabled(isMicOnRef.current);

        streamSourceRef.current?.stop();
        streamSourceRef.current = null;

        if (publisherRef.current) {
          publisherRef.current.element = livekitSession.attachLocalPreview(publisherContainerRef.current);
        }
        setHasSwitchedToLiveVideo(true);
        setMediaNotice('Switched to your live camera.');
        return true;
      } catch (error) {
        setMediaNotice('Could not switch to your camera. Check camera and microphone permissions.');
        return false;
      } finally {
        setIsSwitchingToLiveVideo(false);
      }
    }

    try {
      nextAudioStream = await OT.getUserMedia({ audioSource: true, videoSource: null });
      const nextAudioTrack = nextAudioStream.getAudioTracks()[0];
      if (!nextAudioTrack) {
        throw new Error('No microphone track available.');
      }

      await currentPublisher.setVideoSource(DEFAULT_CAMERA_ID);
      await currentPublisher.setAudioSource(nextAudioTrack);

      currentPublisher.publishVideo(isVideoOnRef.current);
      currentPublisher.publishAudio(isMicOnRef.current);

      // Real sources are live now, so release the clip.
      stopManagedAudioStream();
      managedAudioStreamRef.current = nextAudioStream;
      streamSourceRef.current?.stop();
      streamSourceRef.current = null;

      syncPublisherPreviewContainer();
      setHasSwitchedToLiveVideo(true);
      setMediaNotice('Switched to your live camera.');
      return true;
    } catch (error) {
      stopMediaStreamTracks(nextAudioStream);
      setMediaNotice('Could not switch to your camera. Check camera and microphone permissions.');
      return false;
    } finally {
      setIsSwitchingToLiveVideo(false);
    }
  }, [
    hasSwitchedToLiveVideo,
    isSwitchingToLiveVideo,
    stopManagedAudioStream,
    stopMediaStreamTracks,
    syncPublisherPreviewContainer,
  ]);

  const handleSelectSpeaker = useCallback(async (speakerOption) => {
    const nextPreference = buildStoredPreference(speakerOption, DEFAULT_SPEAKER_ID, 'Default speaker');
    const didApply = await applySpeakerOutputPreference(nextPreference);

    if (didApply) {
      setSpeakerPreference(nextPreference);
    }

    return didApply;
  }, [applySpeakerOutputPreference]);

  const handleSelectCamera = useCallback(async (cameraOption) => {
    const currentPublisher = publisherRef.current;

    if (!currentPublisher || !cameraOption) {
      return false;
    }

    const requestedVideoSourceId = cameraOption.deviceId || DEFAULT_CAMERA_ID;

    const livekitSession = livekitSessionRef.current;
    if (livekitSession) {
      try {
        await livekitSession.setVideoSource(
          requestedVideoSourceId === DEFAULT_CAMERA_ID ? undefined : requestedVideoSourceId
        );
        await livekitSession.setCameraEnabled(isVideoOnRef.current);
        if (publisherRef.current) {
          publisherRef.current.element = livekitSession.attachLocalPreview(publisherContainerRef.current);
        }
        return true;
      } catch (error) {
        return false;
      }
    }

    try {
      if (requestedVideoSourceId === DEFAULT_CAMERA_ID) {
        try {
          await currentPublisher.setVideoSource(DEFAULT_CAMERA_ID);
        } catch (defaultError) {
          const availableDevices = await listCameraDevices();
          const fallbackDevice = availableDevices.find((device) => device.deviceId && device.deviceId !== DEFAULT_CAMERA_ID);

          if (!fallbackDevice?.deviceId) {
            throw defaultError;
          }

          await currentPublisher.setVideoSource(fallbackDevice.deviceId);
        }
      } else {
        await currentPublisher.setVideoSource(requestedVideoSourceId);
      }

      currentPublisher.publishVideo(isVideoOn);
      syncPublisherPreviewContainer();
      return true;
    } catch (error) {
      return false;
    }
  }, [isVideoOn, listCameraDevices, syncPublisherPreviewContainer]);

  const profile = patientProfile || {};
  const patientPreferredName = firstProfileValue(
    profile?.preferred_name,
    profile?.patient_preferred_name,
    callData?.patientPreferredName
  );
  const hasPatientPreferredName = patientPreferredName && String(patientPreferredName).trim().toUpperCase() !== 'N/A';
  const patientPhn = firstProfileValue(profile?.phn, profile?.phn_no, profile?.phn_number, profile?.personal_health_number);
  const patientGender = firstProfileValue(profile?.gender, profile?.sex, profile?.gender_id);
  const isFemalePatient = ['f', 'female'].includes(String(patientGender ?? '').trim().toLowerCase());
  const patientDob = firstProfileValue(profile?.formatted_dob_display);
  const patientAllergy = firstProfileValue(
    profile?.allergy,
    profile?.paallergy,
    profile?.allergies,
    profile?.allergy_note
  );
  const patientAddress = buildPatientAddress(profile);

  const enrichedMedications = useMemo(() => {
    const prescriptionOrderMap = new Map(
      dailyPrescriptions.map((item, index) => [normalizeRx(item?.rx_number || item?.rxnumber), index])
    );
    const prescriptionMap = new Map(
      dailyPrescriptions.map((item) => [normalizeRx(item?.rx_number || item?.rxnumber), item])
    );

    const merged = rawActiveMedications.map((medication) => {
      const dinValue =
        medication?.din ??
        medication?.DIN ??
        medication?.din_number ??
        medication?.dinNumber ??
        medication?.drug_din ??
        medication?.drugDin ??
        null;

      const rx = normalizeRx(medicationRx(medication));
      const prescription = prescriptionMap.get(rx);

      return {
        ...medication,
        id: prescription?.id ?? medication?.id ?? medication?.winrx_data_id ?? medication?.winrxdataid ?? null,
        rxnumber: medicationRx(medication),
        drug: prescription?.drug || medication?.drug || medicationName(medication),
        sig: prescription?.sig || medicationSig(medication),
        processing: prescription?.processing ?? medicationQty(medication),
        narcotic: isNarcoticMedication(prescription) || isNarcoticMedication(medication),
        color: prescription?.color || medication?.color,
        status: prescription?.status || medication?.status || medication?.delivery_status,
        din: dinValue,
        rx_witness: prescription?.rx_witness ?? medication?.rx_witness,
        pending_counseling: prescription?.pending_counseling ?? medication?.pending_counseling,
        pending_counseling_note: prescription?.pending_counseling_note ?? medication?.pending_counseling_note,
        counsling_provided: prescription?.counsling_provided ?? medication?.counsling_provided,
        consult_followup_note: prescription?.consult_followup_note ?? medication?.consult_followup_note,
        f_sign_pharmasist_id: prescription?.f_sign_pharmasist_id ?? medication?.f_sign_pharmasist_id,
        f_pharmasist_sign_name: prescription?.f_pharmasist_sign_name ?? medication?.f_pharmasist_sign_name,
        pharmacist_signature_url: prescription?.pharmacist_signature_url ?? medication?.pharmacist_signature_url,
        drug_counselling_sheet: prescription?.drug_counselling_sheet ?? medication?.drug_counselling_sheet ?? '',
        safe_in_pregnancy: prescription?.safe_in_pregnancy ?? medication?.safe_in_pregnancy ?? '',
        food_instructions: prescription?.food_instructions ?? medication?.food_instructions ?? '',
        quick_counselling_tips: prescription?.quick_counselling_tips ?? medication?.quick_counselling_tips ?? [],
        // Controls "Save Consultation" button visibility.
        // Must come directly from the API response.
        is_counselling: prescription?.is_counselling ?? prescription?.is_counseling ?? medication?.is_counselling ?? medication?.is_counseling ?? false,
      };
    });

    const existingRx = new Set(merged.map((item) => normalizeRx(item?.rxnumber)));
    const prescriptionOnly = dailyPrescriptions
      .filter((item) => !existingRx.has(normalizeRx(item?.rx_number || item?.rxnumber)))
      .map((item) => ({
        id: item?.id ?? item?.winrx_data_id ?? null,
        rxnumber: item?.rx_number || item?.rxnumber,
        drug: item?.drug || 'Medication',
        sig: item?.sig || 'No sig available',
        processing: item?.processing ?? null,
        narcotic: isNarcoticMedication(item),
        color: item?.color,
        status: item?.status || item?.delivery_status,
        din:
          item?.din ??
          item?.DIN ??
          item?.din_number ??
          item?.dinNumber ??
          item?.drug_din ??
          item?.drugDin ??
          null,
        rx_witness: item?.rx_witness,
        pending_counseling: item?.pending_counseling,
        pending_counseling_note: item?.pending_counseling_note,
        counsling_provided: item?.counsling_provided,
        consult_followup_note: item?.consult_followup_note,
        f_sign_pharmasist_id: item?.f_sign_pharmasist_id,
        f_pharmasist_sign_name: item?.f_pharmasist_sign_name,
        pharmacist_signature_url: item?.pharmacist_signature_url,
        drug_counselling_sheet: item?.drug_counselling_sheet ?? '',
        safe_in_pregnancy: item?.safe_in_pregnancy ?? '',
        food_instructions: item?.food_instructions ?? '',
        quick_counselling_tips: item?.quick_counselling_tips ?? [],
        // Controls "Save Consultation" button visibility.
        // Must come directly from the API response.
        is_counselling: item?.is_counselling ?? item?.is_counseling ?? false,
      }));

    return [...merged, ...prescriptionOnly]
      .filter((item) => medicationHasDisplayData(item))
      .sort((left, right) => {
        const narcoticSort = Number(isNarcoticMedication(right)) - Number(isNarcoticMedication(left));
        if (narcoticSort !== 0) return narcoticSort;

        const leftOrder = prescriptionOrderMap.get(normalizeRx(left?.rxnumber));
        const rightOrder = prescriptionOrderMap.get(normalizeRx(right?.rxnumber));
        if (leftOrder != null || rightOrder != null) {
          return (leftOrder ?? Number.MAX_SAFE_INTEGER) - (rightOrder ?? Number.MAX_SAFE_INTEGER);
        }

        return String(medicationName(left)).localeCompare(String(medicationName(right)));
      });
  }, [dailyPrescriptions, rawActiveMedications]);

  const signablePrescriptionIds = useMemo(() => {
    const ids = enrichedMedications
      .map((medication) => medicationPrescriptionId(medication))
      .filter((id) => id !== null && id !== undefined && id !== '')
      .map((id) => String(id));

    return Array.from(new Set(ids));
  }, [enrichedMedications]);

  const verificationStamp = useMemo(
    () => formatVerificationStamp(patientVerification?.verified_at ?? patientVerification?.recorded_at),
    [patientVerification?.recorded_at, patientVerification?.verified_at]
  );

  const counsellingOptions = useMemo(() => ([
    { label: 'Call made', value: 'Call made' },
    { label: 'In person', value: 'In person' },
    { label: 'Written', value: 'Written' },
    { label: 'Virtual', value: 'Virtual' },
    { label: 'Consultation provided', value: 'Consultation provided' },
  ]), []);

  const selectedCounsellingMedication = useMemo(() => {
    if (!activeCounsellingRx) return null;
    const baseMedication =
      enrichedMedications.find((medication) => normalizeRx(medication?.rxnumber) === activeCounsellingRx) ??
      null;

    if (!baseMedication) return null;

    if (!activeDinMedicationDetail) return baseMedication;

    return {
      ...baseMedication,
      drug_counselling_sheet: activeDinMedicationDetail?.drug_counselling_sheet ?? baseMedication?.drug_counselling_sheet ?? '',
      safe_in_pregnancy: activeDinMedicationDetail?.safe_in_pregnancy ?? baseMedication?.safe_in_pregnancy ?? '',
      food_instructions: activeDinMedicationDetail?.food_instructions ?? baseMedication?.food_instructions ?? '',
      quick_counselling_tips: activeDinMedicationDetail?.quick_counselling_tips ?? baseMedication?.quick_counselling_tips ?? [],
      images: activeDinMedicationDetail?.images ?? baseMedication?.images ?? [],
    };
  }, [activeCounsellingRx, enrichedMedications, activeDinMedicationDetail]);

  const sanitizedCounsellingHtml = useMemo(() => ({
    drugCounsellingSheet: sanitizeHtml(selectedCounsellingMedication?.drug_counselling_sheet),
    foodInstructions: sanitizeHtml(selectedCounsellingMedication?.food_instructions),
    safeInPregnancy: sanitizeHtml(selectedCounsellingMedication?.safe_in_pregnancy),
  }), [
    selectedCounsellingMedication?.drug_counselling_sheet,
    selectedCounsellingMedication?.food_instructions,
    selectedCounsellingMedication?.safe_in_pregnancy,
  ]);

  useEffect(() => {
    activeCounsellingRxRef.current = activeCounsellingRx;
  }, [activeCounsellingRx]);

  useEffect(() => {
    setActiveCounsellingRx(null);
    setActiveDinMedicationDetail(null);
    setDinMedicationLoadingRx('');
  }, [rxListTab]);

  const selectedConsultationValue = activeCounsellingRx ? (counsellingSelections[activeCounsellingRx] ?? '') : '';
  const showVirtualCounsellingDetails = true;
  const isCounsellingRequired = isTruthyMedicationFlag(selectedCounsellingMedication?.is_counselling);
  const isCounsellingProvided = isTruthyMedicationFlag(selectedCounsellingMedication?.counsling_provided);
  const canShowSaveConsultation =
    isCounsellingRequired &&
    !isCounsellingProvided;
  const quickCounsellingTips = useMemo(
    () => getQuickCounsellingTips(selectedCounsellingMedication?.quick_counselling_tips),
    [selectedCounsellingMedication?.quick_counselling_tips]
  );

  const counsellingDrugImages = useMemo(() => {
    const raw = selectedCounsellingMedication?.images;
    if (!Array.isArray(raw)) return [];
    return raw.filter((url) => typeof url === 'string' && url.trim().length > 0);
  }, [selectedCounsellingMedication?.images]);
  const visibleCounsellingDrugImages = useMemo(
    () => counsellingDrugImages.slice(0, 4),
    [counsellingDrugImages]
  );

  useEffect(() => {
    setCounsellingDrugImagePreview(null);
  }, [activeCounsellingRx]);

  useEffect(() => {
    if (!counsellingDrugImagePreview) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setCounsellingDrugImagePreview(null);
    };
    window.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [counsellingDrugImagePreview]);

  useEffect(() => {
    if (!showVirtualCounsellingDetails) setCounsellingDrugImagePreview(null);
  }, [showVirtualCounsellingDetails]);

  useEffect(() => {
    if (
      !showVirtualCounsellingDetails ||
      !counsellingDrugImagePreview ||
      visibleCounsellingDrugImages.length === 0
    ) {
      return undefined;
    }

    const isEditableTarget = (target) =>
      target instanceof HTMLElement &&
      (
        target.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
      );

    const handleImageBrowseKey = (event) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (isEditableTarget(event.target)) return;
      const isNextKey = event.key === '>' || (event.shiftKey && event.code === 'Period');
      const isPrevKey = event.key === '<' || (event.shiftKey && event.code === 'Comma');
      if (!isNextKey && !isPrevKey) return;

      event.preventDefault();
      setCounsellingDrugImagePreview((currentPreview) => {
        const currentIndex = visibleCounsellingDrugImages.findIndex((image) => image === currentPreview);
        if (currentIndex < 0) return currentPreview;

        if (isNextKey) {
          return visibleCounsellingDrugImages[(currentIndex + 1) % visibleCounsellingDrugImages.length];
        }

        return visibleCounsellingDrugImages[
          (currentIndex - 1 + visibleCounsellingDrugImages.length) % visibleCounsellingDrugImages.length
        ];
      });
    };

    window.addEventListener('keydown', handleImageBrowseKey);
    return () => {
      window.removeEventListener('keydown', handleImageBrowseKey);
    };
  }, [showVirtualCounsellingDetails, counsellingDrugImagePreview, visibleCounsellingDrugImages]);

  useEffect(() => {
    if (!counsellingSaveError) return undefined;

    const timeoutId = window.setTimeout(() => {
      setCounsellingSaveError('');
    }, 3000);

    return () => window.clearTimeout(timeoutId);
  }, [counsellingSaveError]);

  useEffect(() => {
    if (!prescriptionSignatureError) return undefined;

    const timeoutId = window.setTimeout(() => {
      setPrescriptionSignatureError('');
    }, 3500);

    return () => window.clearTimeout(timeoutId);
  }, [prescriptionSignatureError]);

  useEffect(() => {
    if (!isSignatureModalOpen) return undefined;

    const timeoutId = window.setTimeout(() => {
      prescriptionSignaturePadRef.current?.clear?.();
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [isSignatureModalOpen]);

  useEffect(() => {
    if (!activeCounsellingRx) return;
    // if (!selectedCounsellingMedication || !medicationIsPendingCounselling(selectedCounsellingMedication)) {
    //   setActiveCounsellingRx(null);
    // }
  }, [activeCounsellingRx, selectedCounsellingMedication]);

  useEffect(() => {
    if (!activeCounsellingRx) return;
    setCounsellingSelections((prev) => {
      if (prev[activeCounsellingRx]) return prev;
      return {
        ...prev,
        [activeCounsellingRx]: 'Virtual',
      };
    });
  }, [activeCounsellingRx]);

  const handleVerifyPatient = async (event) => {
    if (patientVerification?.verified || isSavingVerification || !callData?.callId) return;

    const shouldVerify = event?.target?.checked;
    if (!shouldVerify) return;

    setIsSavingVerification(true);
    try {
      const response = await verifyPortalDeliveryVideoCallPatient(callData.callId);
      const nextVerifiedStamp = response?.verified_stamp ?? response?.patient_verification?.verified_stamp ?? '';
      setPatientVerification(
        normalizePatientVerificationPayload(
          response?.patient_verification ?? response?.patientVerification ?? null,
          nextVerifiedStamp
        )
      );
      setVerifiedStamp(nextVerifiedStamp);
    } catch (error) {
      console.error('Error verifying patient during portal delivery call:', error);
    } finally {
      setIsSavingVerification(false);
    }
  };

  const openPrescriptionSignatureModal = () => {
    if (signablePrescriptionIds.length === 0) return;

    setActiveSigningPrescription({
      ids: signablePrescriptionIds,
      count: signablePrescriptionIds.length,
    });
    setPrescriptionSignatureError('');
    setIsSignatureModalOpen(true);
  };

  const clearPrescriptionSignaturePad = () => {
    prescriptionSignaturePadRef.current?.clear?.();
    setPrescriptionSignatureError('');
  };

  const applySignedPrescriptionUpdates = (response) => {
    const signedPrescriptions = Array.isArray(response?.signed_prescriptions)
      ? response.signed_prescriptions
      : [];
    const signatureUrl = response?.signature_url;
    const signedById = new Map(
      signedPrescriptions.map((prescription) => [
        String(prescription?.id ?? prescription?.winrx_data_id),
        prescription,
      ])
    );

    const patchMedication = (item) => {
      const prescriptionId = medicationPrescriptionId(item);
      const signed = signedById.get(String(prescriptionId));
      if (!signed) return item;

      return {
        ...item,
        f_sign_pharmasist_id: signed.f_sign_pharmasist_id ?? response?.pharmacist_id,
        f_pharmasist_sign_name: signed.f_pharmasist_sign_name ?? response?.signature_img,
        pharmacist_signature_url: signatureUrl ?? item?.pharmacist_signature_url,
      };
    };

    setDailyPrescriptions((previous) => previous.map(patchMedication));
    setRawActiveMedications((previous) => previous.map(patchMedication));
  };

  const handleSavePrescriptionSignature = async () => {
    if (!callData?.callId || isSavingPrescriptionSignature) return;

    const prescriptionIds = Array.isArray(activeSigningPrescription?.ids)
      ? activeSigningPrescription.ids
      : [];

    if (prescriptionIds.length === 0) {
      setPrescriptionSignatureError('No prescriptions available to sign.');
      return;
    }

    if (prescriptionSignaturePadRef.current?.isEmpty?.()) {
      setPrescriptionSignatureError('Draw a signature before saving.');
      return;
    }

    setIsSavingPrescriptionSignature(true);
    setPrescriptionSignatureError('');

    try {
      let signatureBase64 = '';
      try {
        signatureBase64 = prescriptionSignaturePadRef.current
          ?.getTrimmedCanvas?.()
          ?.toDataURL('image/png');
      } catch (readError) {
        console.error('Error reading prescription signature canvas:', readError);
      }

      if (!signatureBase64) {
        setPrescriptionSignatureError('Unable to read the signature. Please try again.');
        return;
      }

      const response = await signPortalDeliveryVideoCallPrescriptions(callData.callId, {
        prescription_ids: prescriptionIds.map((id) => Number(id)).filter((id) => Number.isFinite(id)),
        signature_base64: signatureBase64,
      });

      applySignedPrescriptionUpdates(response);
      setActiveSigningPrescription(null);
      setIsSignatureModalOpen(false);
      prescriptionSignaturePadRef.current?.clear?.();
    } catch (error) {
      console.error('Error signing prescriptions during portal delivery call:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Unable to save prescription signature right now.';
      setPrescriptionSignatureError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSavingPrescriptionSignature(false);
    }
  };

  const handleSaveCounselling = async () => {
    if (!callData?.callId || isSavingCounselling) return;

    if (!selectedConsultationValue) {
      setCounsellingSaveError('Select a consultation value first.');
      return;
    }

    if (!selectedCounsellingMedication?.id) {
      setCounsellingSaveError('Unable to find the prescription record for this consultation.');
      return;
    }

    setIsSavingCounselling(true);
    setCounsellingSaveError('');

    try {
      const response = await savePortalDeliveryVideoCallCounselling(callData.callId, {
        winrx_data_id: selectedCounsellingMedication.id,
        consult_followup_note: selectedConsultationValue,
      });
      const successMessage =
        response?.message ||
        response?.data?.message ||
        'Consultation saved successfully.';

      setDailyPrescriptions((prev) =>
        prev.map((item) => {
          const matchesId = String(item?.id ?? item?.winrx_data_id ?? '') === String(selectedCounsellingMedication.id);
          const matchesRx = normalizeRx(item?.rx_number || item?.rxnumber) === normalizeRx(selectedCounsellingMedication?.rxnumber);

          if (!matchesId && !matchesRx) return item;

          return {
            ...item,
            counsling_provided: 1,
            consult_followup_note: response?.consult_followup_note ?? selectedConsultationValue,
            new_counselling_time: response?.new_counselling_time ?? new Date().toISOString(),
          };
        })
      );
      toast.success(successMessage);

      setActiveCounsellingRx(null);
      setActiveDinMedicationDetail(null);
      setDinMedicationLoadingRx('');
    } catch (error) {
      console.error('Error saving portal video-call consultation:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Unable to save consultation right now.';
      setCounsellingSaveError(
        errorMessage
      );
      toast.error(errorMessage);
    } finally {
      setIsSavingCounselling(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-[radial-gradient(circle_at_top,rgba(59,130,246,0.24),transparent_28%),linear-gradient(135deg,#020617_0%,#0f172a_42%,#13233d_100%)]">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_80%_20%,rgba(168,85,247,0.12),transparent_24%),radial-gradient(circle_at_15%_80%,rgba(34,197,94,0.09),transparent_20%)]" />

      <div className="relative flex h-full flex-col lg:flex-row">
        <aside className="w-full border-b border-slate-200/80 bg-[linear-gradient(180deg,#f8fafc_0%,#eef4ff_100%)] shadow-[20px_0_60px_rgba(15,23,42,0.12)] transition-all duration-300 ease-out lg:w-[380px] lg:border-b-0 lg:border-r">
          <div className="h-full overflow-y-auto p-4 commonscrollbarhide">
            <div className="flex flex-col gap-3">
              <div className="transition-all duration-300 ease-out">
                <h1 className="text-[30px] font-semibold leading-tight text-slate-950">{patientDisplayName}</h1>
                {hasPatientPreferredName ? (
                  <p className="mt-1 text-sm font-medium text-slate-500">
                    Preferred Name: {patientPreferredName}
                  </p>
                ) : null}
                {callData?.callId ? (
                  <div
                    className={`mt-2 inline-flex w-fit max-w-full flex-col gap-0.5 rounded-xl border px-2 py-1 transition-colors ${patientVerification?.verified
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-slate-300 bg-white text-slate-700'
                      }`}
                  >
                    <label className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em]">
                      <input
                        type="checkbox"
                        className="h-[14px] w-[14px] rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                        checked={Boolean(patientVerification?.verified)}
                        disabled={Boolean(patientVerification?.verified) || isSavingVerification}
                        onChange={handleVerifyPatient}
                      />
                      <span>{isSavingVerification ? 'Saving…' : 'Patient Verified'}</span>
                    </label>
                    {patientVerification?.verified && (
                      <div className="flex flex-nowrap items-center gap-1.5 pl-5 text-[10px] font-semibold text-slate-700 whitespace-nowrap -mt-[4px]">
                        <span>Verified by {patientVerification?.verified_by_name || 'Pharmacist'}</span>
                        {verificationStamp.datePart ? (
                          <span>{verificationStamp.datePart}</span>
                        ) : null}
                        {verificationStamp.timePart ? (
                          <span>{verificationStamp.timePart}</span>
                        ) : null}
                        {!verificationStamp.datePart && !verificationStamp.timePart && verifiedStamp ? (
                          <span>{verifiedStamp}</span>
                        ) : null}
                      </div>
                    )}
                  </div>
                ) : null}
                {isPatientDataLoading ? (
                  <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
                    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-sky-600" aria-hidden />
                    <span>Loading patient details…</span>
                  </div>
                ) : (
                  <>
                    <div className="mt-3 flex items-center gap-4 overflow-x-auto whitespace-nowrap text-sm text-slate-700 commonscrollbarhide">
                      {patientPhn && String(patientPhn).trim() !== '' && String(patientPhn).trim().toUpperCase() !== 'N/A' && (
                        <span className="flex items-center gap-1.5 shrink-0" title="PHN">
                          <VideoCallPhnIcon className="w-4 h-4 shrink-0 text-slate-500" color="currentColor" />
                          <span className="font-medium text-slate-950">{patientPhn}</span>
                        </span>
                      )}
                      {patientGender && String(patientGender).trim() !== '' && String(patientGender).trim().toUpperCase() !== 'N/A' && (
                        <span className="flex items-center gap-1.5 shrink-0" title="Gender">
                          <VideoCallGenderIcon className="w-4 h-4 shrink-0 text-slate-500" color="currentColor" />
                          <span className="font-medium text-slate-950">{patientGender}</span>
                        </span>
                      )}
                      {patientDob && String(patientDob).trim() !== '' && String(patientDob).trim().toUpperCase() !== 'N/A' && (
                        <span className="flex items-center gap-1.5 shrink-0" title="DOB">
                          <VideoCallDobIcon className="w-4 h-4 shrink-0 text-slate-500" color="currentColor" />
                          <span className="font-medium text-slate-950">{patientDob}</span>
                        </span>
                      )}
                    </div>
                    {patientAddress ? (
                      <div className="mt-2 flex items-start gap-1.5 text-sm text-slate-700">
                        <VideoCallAddressIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" color="currentColor" />
                        <span className="font-medium leading-5 text-slate-950 break-words">{patientAddress}</span>
                      </div>
                    ) : null}
                    {patientAllergy && String(patientAllergy).trim() !== '' && String(patientAllergy).trim().toUpperCase() !== 'N/A' ? (
                      <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-slate-700">
                        <span className="font-semibold uppercase tracking-[0.14em] text-amber-700">Allergy</span>
                        <div className="mt-1 font-medium leading-5 text-slate-950 break-words">{patientAllergy}</div>
                      </div>
                    ) : null}
                  </>
                )}
              </div>

            </div>

            {/* Medication list tabs (API triggers only; do not change existing list rendering yet) */}
            <div className="mt-3 flex gap-2 rounded-lg border border-slate-200 bg-white p-1">
              <button
                type="button"
                onClick={async () => {
                  setRxListTab('Active');
                  await loadActiveMedicationData(callData?.patientId);
                }}
                className={`flex-1 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${rxListTab === 'Active' ? 'bg-blue-50 text-blue-700' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                aria-selected={rxListTab === 'Active'}
              >
                Active
              </button>
              <button
                type="button"
                onClick={async () => {
                  setRxListTab('All');
                  await loadAllMedicationData(callData?.patientId);
                }}
                className={`flex-1 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${rxListTab === 'All' ? 'bg-blue-50 text-blue-700' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                aria-selected={rxListTab === 'All'}
              >
                All
              </button>
            </div>

            <section className="mt-3 rounded-lg border border-slate-200 bg-white  shadow-[0_14px_40px_rgba(15,23,42,0.08)]">
              <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Prescriptions</p>
                </div>
                <button
                  type="button"
                  onClick={openPrescriptionSignatureModal}
                  disabled={signablePrescriptionIds.length === 0 || isSavingPrescriptionSignature}
                  className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md bg-[#2f55d4] px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm transition hover:bg-[#2447ba] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
                >
                  <FileSignature className="h-3.5 w-3.5" />
                  Sign
                </button>
              </div>
              <div className=" overflow-y-auto commonscrollbarhide">
                {isMedicationListLoading ? (
                  <div className="flex flex-col items-center justify-center gap-2 py-8 text-slate-500">
                    <span className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-sky-600" aria-hidden />
                    <p className="text-sm">Loading medications…</p>
                  </div>
                ) : enrichedMedications.length === 0 ? (
                  <p className="py-3 text-sm text-slate-500">
                    {rxListTab === 'All' ? 'No medications found.' : 'No active medications found.'}
                  </p>
                ) : (
                  <>
                    {enrichedMedications.map((medication, index) => {
                      const canOpenPendingCounselling = rxListTab !== 'All';
                      const isPrescriptionSigned = medicationHasPharmacistSignature(medication);
                      const openPendingCounsellingForCard = () => {
                        if (!canOpenPendingCounselling) return;
                        const rx = normalizeRx(medication?.rxnumber);
                        if (!rx) return;

                        setActiveCounsellingRx(rx);
                        setActiveDinMedicationDetail(null);
                        setDinMedicationLoadingRx(rx);

                        // Trigger DIN-based lookup on every RX card click (fire-and-forget).
                        const dinValue =
                          medication?.din ??
                          medication?.DIN ??
                          medication?.din_number ??
                          medication?.dinNumber ??
                          medication?.drug_din ??
                          medication?.drugDin;
                        const din = dinValue !== undefined && dinValue !== null ? String(dinValue).trim() : '';
                        if (din) {
                          void fetchPatientMedicineByDin(din)
                            .then((response) => {
                              const detail = getDinMedicationDetail(response);
                              if (detail && activeCounsellingRxRef.current === rx) {
                                setActiveDinMedicationDetail(detail);
                              }
                            })
                            .catch(() => { })
                            .finally(() => {
                              setDinMedicationLoadingRx((currentRx) => (currentRx === rx ? '' : currentRx));
                            });
                          return;
                        }

                        setDinMedicationLoadingRx('');
                      };

                      return (
                        <div
                          key={`${normalizeRx(medication?.rxnumber)}-${index}`}
                          role={canOpenPendingCounselling ? 'button' : undefined}
                          tabIndex={canOpenPendingCounselling ? 0 : undefined}
                          title={canOpenPendingCounselling ? 'Open pending counselling' : undefined}
                          onClick={canOpenPendingCounselling ? openPendingCounsellingForCard : undefined}
                          onKeyDown={(event) => {
                            if (!canOpenPendingCounselling) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              openPendingCounsellingForCard();
                            }
                          }}
                          className={`px-3 ${canOpenPendingCounselling ? 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2' : ''} border-b border-slate-200 py-1.5 last:border-b-0 transition-colors ${canOpenPendingCounselling && activeCounsellingRx === normalizeRx(medication?.rxnumber)
                            ? 'bg-blue-50 '
                            : ''
                            } ${medication?.color ? 'border-l-4 pl-2' : ''}`}
                          style={medication?.color ? { borderLeftColor: medication.color } : undefined}
                        >

                          <div className="flex items-center justify-center">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex min-w-0 items-center gap-2">
                                  <p className="text-[12px] font-semibold leading-5 text-slate-950">
                                    <span className="truncate">{medication?.rxnumber || ''}</span>
                                  </p>
                                  {isPrescriptionSigned ? (
                                    <span className="inline-flex items-center rounded-sm border border-emerald-400 bg-emerald-50 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.08em] text-emerald-700">
                                      Signed
                                    </span>
                                  ) : null}
                                  {medicationHasWitness(medication) ? (
                                    <span className="inline-flex items-center rounded-sm border border-red-400 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.08em] text-red-600">
                                      Witness
                                    </span>
                                  ) : null}
                                </div>


                                <div className="mt-1 flex flex-col items-end gap-2 text-[11px] font-medium text-slate-600">
                                  <span className="shrink-0">Qty {formatMedicationQty(medicationQty(medication))}</span>
                                </div>
                              </div>

                              <div className="mt-0.5 flex items-start justify-between gap-2">
                                <p className="flex-1 text-[12px] font-semibold leading-5 text-slate-950">
                                  {(() => {
                                    const drugLabel = medication?.drug || medicationName(medication);
                                    const dinLabel = medicationDin(medication);
                                    return dinLabel ? `${drugLabel} - ${dinLabel}` : drugLabel;
                                  })()}
                                </p>
                                {/* {medicationIsPendingCounselling(medication) ? ( */}

                                {/* ) : null} */}
                              </div>


                              <p className="mt-0.5 break-words text-[11px] leading-4 text-slate-700">
                                {medication?.sig || medicationSig(medication)}
                              </p>


                            </div>
                            <div className="ml-auto pl-4">

                              {canOpenPendingCounselling ? (
                                <span
                                  className={`pointer-events-none inline-flex shrink-0 items-center justify-center transition ${activeCounsellingRx === normalizeRx(medication?.rxnumber)
                                    ? 'text-slate-300'
                                    : 'text-slate-200'
                                    }`}
                                  aria-hidden="true"
                                >
                                  <svg viewBox="0 0 512 512" fill="currentColor" className="h-4 w-4">
                                    <path d="m121.373 457.373 201.372-201.373-201.372-201.373a32 32 0 0 1 45.254-45.254l224 224a32 32 0 0 1 0 45.254l-224 224a32 32 0 0 1 -45.254-45.254z" />
                                  </svg>
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </section>
          </div>
        </aside>

        <section
          className={`overflow-hidden border-slate-200/80 bg-[linear-gradient(180deg,#f8fafc_0%,#eef4ff_100%)] shadow-[10px_0_40px_rgba(15,23,42,0.1)] transition-all duration-300 ease-out lg:border-r ${selectedCounsellingMedication
            ? 'max-h-[420px] border-t border-b px-4 py-3 opacity-100 lg:max-h-none lg:w-[580px] lg:border-t-0 lg:border-b-0'
            : 'max-h-0 border-0 px-0 py-0 opacity-0 lg:max-h-none lg:w-0'
            }`}
        >
          {selectedCounsellingMedication ? (
            <div className="relative flex max-h-[70vh] flex-col lg:max-h-[calc(100vh-2rem)]">
              <div className="flex items-start gap-3 border-b border-slate-200 pb-3">
                <div className="min-w-0 flex-1">
                  {isCounsellingProvided ? (
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded-[3px] border border-green-500 bg-green-50 text-green-600">
                        <svg viewBox="0 0 20 20" fill="currentColor" className="h-3 w-3" aria-hidden="true">
                          <path
                            fillRule="evenodd"
                            d="M16.704 5.29a1 1 0 01.006 1.414l-7.2 7.3a1 1 0 01-1.425.01L3.29 9.404A1 1 0 014.7 7.99l3.074 3.063 6.496-6.58a1 1 0 011.434-.183z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                      <span>Counselling</span>
                    </div>
                  ) : isCounsellingRequired ? (
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-700">Pending Counselling</p>
                  ) : null}
                  <h3 className="mt-1 shrink-0 whitespace-nowrap text-lg font-semibold text-slate-950">
                    RX {selectedCounsellingMedication?.rxnumber}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setActiveCounsellingRx(null);
                    setActiveDinMedicationDetail(null);
                    setDinMedicationLoadingRx('');
                  }}
                  className="ml-auto inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                  aria-label="Close consultation panel"
                >
                  <CloseRoundedIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>

              <div className="mt-4 space-y-4 overflow-y-auto pr-1">
                {showVirtualCounsellingDetails ? (
                  <>
                    {dinMedicationLoadingRx === activeCounsellingRx ? (
                      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                        <div className="flex flex-col items-center justify-center gap-2 py-8 text-slate-500">
                          <span className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-sky-600" aria-hidden />
                          <p className="text-sm">Loading counselling sheet…</p>
                        </div>
                      </div>
                    ) : null}

                    {dinMedicationLoadingRx !== activeCounsellingRx ? (
                      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                        <div className="flex w-full flex-nowrap items-center justify-between gap-2.5">
                          <div className="flex w-fit shrink-0 flex-col rounded-xl  border-slate-200 bg-white ">
                            <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-700">
                              Consultation
                            </p>
                            <div className="w-fit shrink-0">
                              <select
                                value={counsellingSelections[activeCounsellingRx] ?? ''}
                                onChange={(event) => {
                                  const { value } = event.target;
                                  setCounsellingSelections((prev) => ({
                                    ...prev,
                                    [activeCounsellingRx]: value,
                                  }));
                                }}
                                className="w-auto min-w-0 rounded-md border border-slate-300 bg-slate-50 px-2.5 py-1.5 text-[13px] font-medium text-slate-900 outline-none transition focus:border-sky-500 focus:bg-white"
                              >
                                <option value="">Select consultation</option>
                                {counsellingOptions.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {visibleCounsellingDrugImages.length > 0 ? (
                            <div className="flex shrink-0 items-center justify-center rounded-xl  border-slate-200 bg-white ">
                              <div className="inline-grid shrink-0 grid-cols-4 gap-1">
                                {visibleCounsellingDrugImages.map((src, index) => (
                                  <button
                                    type="button"
                                    key={`${src}-${index}`}
                                    onClick={() => setCounsellingDrugImagePreview(src)}
                                    className={`flex h-16 w-16 border cursor-pointer items-center justify-center overflow-hidden rounded-md  bg-white  outline-none ring-0 transition hover:border-sky-500 focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 ${counsellingDrugImagePreview === src ? 'border-sky-500' : 'border-slate-100'}`}
                                    aria-pressed={counsellingDrugImagePreview === src}
                                  >
                                    <img src={src} alt="" className="h-full w-full rounded-[6px] object-cover" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : null}

                    {dinMedicationLoadingRx !== activeCounsellingRx && quickCounsellingTips.length > 0 ? (
                      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                        <p className="text-[14px] font-bold uppercase tracking-[0.16em] text-emerald-700">Quick Counselling Tips</p>
                        <ul className="mt-2 space-y-1 text-[13px] font-normal leading-5 text-slate-900">
                          {quickCounsellingTips.map((tip, index) => (
                            <li key={`${tip}-${index}`} className="flex items-center gap-2">
                              <CheckCircleSvgIcon className="h-[10px] w-[10px] shrink-0" />
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}

                    {dinMedicationLoadingRx !== activeCounsellingRx && (sanitizedCounsellingHtml.drugCounsellingSheet || sanitizedCounsellingHtml.foodInstructions) ? (
                      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                        {sanitizedCounsellingHtml.drugCounsellingSheet ? (
                          <div
                            className="max-w-none text-[13px] font-normal leading-5 text-slate-900 [&_*]:text-[13px] [&_*]:leading-5 [&_p]:font-normal [&_li]:font-normal [&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-[14px] [&_h1]:font-bold [&_h1]:leading-5 [&_h1:first-child]:mt-0 [&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-[14px] [&_h2]:font-bold [&_h2]:leading-5 [&_h2:first-child]:mt-0 [&_h3]:mt-4 [&_h3]:mb-2 [&_h3]:text-[14px] [&_h3]:font-bold [&_h3]:leading-5 [&_h3:first-child]:mt-0 [&_h4]:mt-4 [&_h4]:mb-2 [&_h4]:text-[14px] [&_h4]:font-bold [&_h4]:leading-5 [&_h4:first-child]:mt-0 [&_h5]:mt-4 [&_h5]:mb-2 [&_h5]:text-[14px] [&_h5]:font-bold [&_h5]:leading-5 [&_h5:first-child]:mt-0 [&_h6]:mt-4 [&_h6]:mb-2 [&_h6]:text-[14px] [&_h6]:font-bold [&_h6]:leading-5 [&_h6:first-child]:mt-0 [&_strong]:text-[14px] [&_strong]:font-bold [&_strong]:leading-5 [&_b]:text-[14px] [&_b]:font-bold [&_b]:leading-5"
                            dangerouslySetInnerHTML={{ __html: sanitizedCounsellingHtml.drugCounsellingSheet }}
                          />
                        ) : null}

                        {sanitizedCounsellingHtml.foodInstructions ? (
                          <div className="mt-4">
                            <p className="mb-2 text-[14px] font-bold leading-5 text-slate-900">With Or Without Food Instructions</p>
                            <div
                              className="mt-2 max-w-none text-[13px] leading-5 text-slate-900 [&_*]:text-[13px] [&_*]:leading-5 [&_p]:font-normal [&_li]:font-normal"
                              dangerouslySetInnerHTML={{ __html: sanitizedCounsellingHtml.foodInstructions }}
                            />
                          </div>
                        ) : null}

                        {isFemalePatient && sanitizedCounsellingHtml.safeInPregnancy ? (
                          <div className="mt-4">
                            <p className="mb-2 text-[14px] font-bold leading-5 text-emerald-700">Safe In Pregnancy</p>
                            <div
                              className="max-w-none text-[13px] leading-5 text-slate-900 [&_*]:text-[13px] [&_*]:leading-5 [&_p]:font-normal [&_li]:font-normal"
                              dangerouslySetInnerHTML={{ __html: sanitizedCounsellingHtml.safeInPregnancy }}
                            />
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                  </>
                ) : null}

                {counsellingSaveError ? (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-600">
                    {counsellingSaveError}
                  </div>
                ) : null}

                {canShowSaveConsultation ? (
                  <button
                    type="button"
                    onClick={handleSaveCounselling}
                    disabled={isSavingCounselling || !selectedConsultationValue}
                    className="inline-flex w-full items-center justify-center rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    {isSavingCounselling ? 'Saving Consultation…' : 'Save Consultation'}
                  </button>
                ) : null}
              </div>

              {counsellingDrugImagePreview ? (
                <div
                  className="absolute inset-0 z-[10020] flex items-start justify-center overflow-y-auto bg-slate-950/65 p-2"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Drug image preview"
                  onClick={() => setCounsellingDrugImagePreview(null)}
                >
                  <div className="relative w-full min-w-0 max-w-md rounded-xl border border-slate-200 bg-white p-2 shadow-xl" onClick={(event) => event.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => setCounsellingDrugImagePreview(null)}
                      className="absolute right-1 top-1 z-10 flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-semibold leading-none text-slate-700 shadow-sm outline-none transition hover:bg-slate-50 focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0"
                      aria-label="Close"
                    >
                      ×
                    </button>
                    <img
                      src={counsellingDrugImagePreview}
                      alt=""
                      className="mx-auto max-h-[min(56vh,420px)] w-full rounded-lg object-contain"
                    />
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </section>

        {isSignatureModalOpen ? (
          <div className="fixed inset-0 z-[10020] flex items-center justify-center bg-slate-950/55 px-4 py-6 backdrop-blur-sm">
            <div className="flex max-h-[calc(100vh-3rem)] w-full max-w-sm flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.35)]">
              <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
                <div className="min-w-0">
                  <h2 className="text-base font-semibold text-slate-950">Sign Prescriptions</h2>
                  <p className="mt-0.5 text-xs font-semibold text-slate-500">
                    {activeSigningPrescription?.count || 0} prescriptions
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (isSavingPrescriptionSignature) return;
                    setIsSignatureModalOpen(false);
                    setActiveSigningPrescription(null);
                    setPrescriptionSignatureError('');
                  }}
                  disabled={isSavingPrescriptionSignature}
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Close
                </button>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                  <SignatureCanvas
                    ref={prescriptionSignaturePadRef}
                    penColor="black"
                    clearOnResize={false}
                    canvasProps={{
                      className: 'h-32 w-full rounded-lg bg-white',
                      'aria-label': 'Pharmacist prescription signature',
                    }}
                  />
                </div>
                {prescriptionSignatureError ? (
                  <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
                    {prescriptionSignatureError}
                  </p>
                ) : null}
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-4 py-3">
                <button
                  type="button"
                  onClick={clearPrescriptionSignaturePad}
                  disabled={isSavingPrescriptionSignature}
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <RotateCcw className="h-4 w-4" />
                  Clear
                </button>
                <button
                  type="button"
                  onClick={handleSavePrescriptionSignature}
                  disabled={isSavingPrescriptionSignature || !activeSigningPrescription?.ids?.length}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-[#2f55d4] px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-[#2447ba] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
                >
                  {isSavingPrescriptionSignature ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving
                    </>
                  ) : (
                    <>
                      <FileSignature className="h-4 w-4" />
                      Save Signature
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <main className={`m-0 w-full max-w-none basis-0 flex-1 transition-all duration-300 ease-out ${selectedCounsellingMedication ? 'p-2 lg:p-2' : 'p-4 lg:p-3'}`}>
          <div className="flex h-full w-full flex-col gap-4">
            <div className={`relative min-w-0 w-full flex-1 transition-all duration-300 ease-out ${selectedCounsellingMedication ? 'lg:scale-[0.965] lg:translate-x-1 lg:opacity-95' : 'lg:scale-100 lg:translate-x-0 lg:opacity-100'} ${isRemotePortrait ? 'flex items-center justify-center' : ''}`}>
              <div className={`relative h-full min-h-[420px] overflow-hidden rounded-xl border border-white/10 bg-black shadow-[0_28px_90px_rgba(2,6,23,0.5)] transition-all duration-300 ease-out ${isRemotePortrait ? 'h-[min(100%,calc(100vh-2.5rem))] w-[min(100%,440px)] max-w-[440px] aspect-[9/16]' : ''}`}>
                <div ref={subscriberContainerRef} className="absolute inset-0" />

                {!connectionError && (
                  <>
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-slate-950/70 via-slate-950/20 to-transparent" />
                    {/* <div className="absolute left-5 top-5">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-slate-950/55 px-3 py-1.5 backdrop-blur-md">
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 shadow-[0_0_14px_rgba(74,222,128,0.8)]" />
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white">{patientDisplayName}</span>
                  </div>
                </div> */}
                    <div
                      className={`absolute top-5 z-20 flex flex-col gap-2 ${isRemotePortrait ? 'left-1/2 w-[min(92%,460px)] -translate-x-1/2' : 'right-5 items-start'
                        }`}
                    >
                      <div
                        className={`rounded-[22px] border border-black/80 bg-slate-900/95 px-4 py-3 text-white shadow-[0_10px_36px_rgba(0,0,0,0.5),0_4px_14px_rgba(0,0,0,0.45),0_1px_3px_rgba(0,0,0,0.35)] backdrop-blur-md ${isRemotePortrait ? 'w-full' : ''}`}
                        style={{ background: '#161515' }}
                      >
                        <p className="text-sm font-semibold ">
                          {callData?.driverName || 'Assistant'} with {pharmacistName || callData?.pharmacistName || 'Pharmacist'}
                        </p>
                        <div className={`mt-2 flex items-center justify-start gap-2 text-[11px] ${isRemotePortrait ? 'flex-nowrap' : 'flex-wrap'}`}>
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-600 px-2.5 py-1 font-bold uppercase tracking-[0.14em] text-white shadow-sm">
                            <span className="h-2 w-2 shrink-0 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.85)]" aria-hidden />
                            Live
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full border border-yellow-300/55 bg-yellow-500/25 px-2.5 py-1 font-semibold text-yellow-50">
                            <svg className="h-3.5 w-3.5 shrink-0 opacity-95" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M6 2a1 1 0 011 1v1h6V3a1 1 0 112 0v1h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18h-10.5A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V3a1 1 0 011-1zm10 7H4v6.25c0 .414.336.75.75.75h10.5a.75.75 0 00.75-.75V9z" clipRule="evenodd" />
                            </svg>
                            {callDateTime.datePart}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full border border-sky-300/55 bg-sky-500/25 px-2.5 py-1 font-semibold text-sky-50">
                            <svg className="h-3.5 w-3.5 shrink-0 opacity-95" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M10 2a8 8 0 100 16 8 8 0 000-16zm1 4a1 1 0 10-2 0v4.5a1 1 0 00.293.707l2.5 2.5a1 1 0 101.414-1.414L11 10.086V6z" clipRule="evenodd" />
                            </svg>
                            {callDateTime.timePart}
                          </span>
                        </div>
                      </div>
                      {autoPickupEnabled && onDisableAutoPickup && (
                        <button
                          type="button"
                          onClick={onDisableAutoPickup}
                          disabled={isAutoPickupUpdating}
                          className="self-start rounded-lg border border-white/15 bg-slate-950/78 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white shadow-[0_10px_24px_rgba(0,0,0,0.28)] backdrop-blur-md transition-colors hover:bg-slate-900/90 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isAutoPickupUpdating ? 'Turning Off...' : 'Turn Off Auto Pickup'}
                        </button>
                      )}
                    </div>
                  </>
                )}
                {isCallAudioBlocked && (
                  <button
                    type="button"
                    onClick={handleEnableCallAudio}
                    className="absolute left-1/2 top-6 z-20 -translate-x-1/2 flex items-center gap-2 rounded-full border border-amber-300/60 bg-amber-400/20 px-5 py-2.5 text-sm font-semibold text-amber-50 shadow-[0_14px_34px_rgba(15,23,42,0.35)] backdrop-blur-md transition-colors hover:bg-amber-400/30"
                  >
                    <MicIcon className="w-4 h-4" color="currentColor" />
                    Enable sound
                  </button>
                )}
                {mediaNotice && (
                  <div className="absolute left-1/2 top-28 z-30 w-[min(92%,520px)] -translate-x-1/2 rounded-[20px] border border-sky-200/30 bg-slate-950/90 px-4 py-3 text-white shadow-[0_18px_40px_rgba(15,23,42,0.25)]">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-200">Call Notice</p>
                    <p className="mt-1 text-sm text-slate-100">{mediaNotice}</p>
                  </div>
                )}

                {connectionError && (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-950/82 p-6 text-center text-sm font-semibold text-rose-200">
                    {connectionError}
                  </div>
                )}

                {isRemotePortrait && (
                  <div className="absolute bottom-5 right-5 h-32 w-24 overflow-hidden rounded-[22px] border border-white/20 bg-slate-900 shadow-2xl">
                    <div ref={publisherContainerRef} className="h-full w-full" />
                  </div>
                )}
              </div>

              <div
                className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center justify-center gap-0.5 py-2 px-4 max-w-[calc(100vw-2rem)]"
                style={{
                  backgroundColor: 'rgba(39, 39, 42, 0.95)',
                  borderColor: 'rgba(255, 255, 255, 0.08)',
                  borderRadius: '9999px',
                  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.4)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                }}
              >
                <div
                  className="mr-1 inline-flex items-center rounded-full border border-white/10 bg-[rgba(24,24,27,0.65)] p-1 shadow-[0_6px_18px_rgba(0,0,0,0.35)]"
                  role="group"
                  aria-label="Audio controls"
                >
                  <ControlButton
                    onClick={() => setIsAudioDevicePanelOpen((previous) => !previous)}
                    active
                    title={isAudioDevicePanelOpen ? 'Hide audio devices' : 'Show audio devices'}
                    className="h-9 w-9 bg-white/5 hover:bg-white/10"
                  >
                    {isAudioDevicePanelOpen
                      ? <KeyboardArrowUpRoundedIcon sx={{ fontSize: 18, color: '#e4e4e7' }} />
                      : <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18, color: '#e4e4e7' }} />}
                  </ControlButton>
                  <div className="mx-1 h-6 w-px bg-white/10" aria-hidden />
                  <ControlButton
                    onClick={toggleMic}
                    active={isMicOn}
                    title={isMicOn ? 'Mute microphone' : 'Unmute microphone'}
                    className={`h-10 w-10 ${isMicOn ? 'bg-white/5 hover:bg-white/10 text-[#e4e4e7]' : 'bg-red-500/25 hover:bg-red-500/30 text-red-200'}`}
                  >
                    {isMicOn
                      ? <MicIcon className="w-5 h-5" color="currentColor" />
                      : <MicOffIcon className="w-5 h-5" color="currentColor" />}
                  </ControlButton>
                </div>
                <ControlButton onClick={toggleVideo} active={isVideoOn} title={isVideoOn ? 'Turn camera off' : 'Turn camera on'}>
                  {isVideoOn ? <VideoIcon className="w-5 h-5" color="currentColor" /> : <VideoOffIcon className="w-5 h-5" color="currentColor" />}
                </ControlButton>
                {usesStreamSource && !hasSwitchedToLiveVideo && (
                  <button
                    type="button"
                    onClick={handleSwitchToLiveVideo}
                    disabled={isSwitchingToLiveVideo}
                    title="Switch to your live camera"
                    className="flex items-center gap-2 rounded-full border border-emerald-300/60 bg-emerald-500/15 px-4 h-10 text-sm font-semibold text-emerald-100 transition-colors hover:bg-emerald-500/25 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <VideoIcon className="w-5 h-5" color="currentColor" />
                    {isSwitchingToLiveVideo ? 'Switching...' : 'Switch to live'}
                  </button>
                )}
                {isPiPSupported && (
                  <ControlButton onClick={requestRemotePictureInPicture} active={!isPiPActive} title="Picture in picture">
                    <PictureInPictureAltRoundedIcon sx={{ fontSize: 20, color: '#e4e4e7' }} />
                  </ControlButton>
                )}
                <ControlButton onClick={onClose} variant="danger" title="End call">
                  <CallEndedIcon className="w-5 h-5" color="currentColor" />
                </ControlButton>
              </div>

              {isAudioDevicePanelOpen && (
                <div
                  className={`absolute bottom-24 left-1/2 z-20 flex -translate-x-1/2 border border-white/10 bg-[rgba(39,39,42,0.97)] shadow-[0_10px_30px_rgba(0,0,0,0.35)] ${
                    isRemotePortrait
                      ? 'w-[min(calc(100%-1.5rem),400px)] flex-col items-stretch gap-2 rounded-[28px] px-3 py-3'
                      : 'w-[min(92vw,960px)] items-center gap-3 rounded-full px-4 py-3'
                  }`}
                >
                  {/* Hidden while a pre-recorded clip is publishing: picking a
                      device would swap the real camera/mic back in. */}
                  {(!usesStreamSource || hasSwitchedToLiveVideo) && (
                    <>
                      <CameraSelector
                        isReady={Boolean(publisherInstance)}
                        listDevices={listCameraDevices}
                        onMediaNotice={setMediaNotice}
                        onSelectDevice={handleSelectCamera}
                        requestPermissions={requestCameraPermission}
                      />
                      <MicrophoneSelector
                        isReady={Boolean(publisherInstance)}
                        listDevices={listMicrophoneDevices}
                        onMediaNotice={setMediaNotice}
                        onSelectDevice={handleSelectMicrophone}
                        requestPermissions={requestMicrophonePermission}
                      />
                    </>
                  )}
                  <SpeakerSelector
                    isReady={Boolean(publisherInstance) && canUseAudioOutputSelection}
                    listDevices={listSpeakerDevices}
                    onMediaNotice={setMediaNotice}
                    onSelectDevice={handleSelectSpeaker}
                  />
                </div>
              )}

              {isAudioDevicePanelOpen && audioOutputSupportMessage && (
                <div className="absolute bottom-14 left-1/2 z-20 w-[min(92vw,640px)] -translate-x-1/2 px-4">
                  <div className="rounded-2xl border border-amber-300/30 bg-amber-50/95 px-4 py-2 text-xs font-medium text-amber-900 shadow-[0_10px_30px_rgba(0,0,0,0.18)]">
                    {audioOutputSupportMessage}
                  </div>
                </div>
              )}

              {!isRemotePortrait && (
                <div className="absolute bottom-5 right-5 h-40 w-28 overflow-hidden rounded-[22px] border border-white/20 bg-slate-900 shadow-2xl lg:h-52 lg:w-40">
                  <div ref={publisherContainerRef} className="h-full w-full" />
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PortalDeliveryCallWorkspace;
